<template>
  <div class="h-screen flex flex-col overflow-hidden fixed inset-0">
    <!-- Compact Header -->
    <div class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 flex-shrink-0 mx-5 rounded-lg shadow-sm mt-4">
      <div class="flex items-center justify-between h-16 px-5">
        <div class="flex items-center space-x-3 flex-1 min-w-0">
          <HLButton
            @click="goBack"
            variant="ghost"
            size="sm"
            class="flex-shrink-0"
            title="Back to Bugs"
          >
            <HLIcon size="20">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </HLIcon>
          </HLButton>
          <div class="h-6 w-px bg-gray-300 dark:bg-gray-600 flex-shrink-0"></div>
          
          <!-- Bug ID Badge -->
          <div :class="bugIdBadgeClasses" class="px-3 py-1 rounded border flex-shrink-0">
            <span :class="bugIdTextClasses" class="text-sm font-semibold">
              {{ bug?.bugId || bug?.id || 'Loading...' }}
            </span>
          </div>
          
          <!-- Title (Editable) -->
          <HLInput
            v-if="editingField === 'title'"
            v-model="editForm.title"
            @blur="saveEdit('title')"
            @keydown.enter="saveEdit('title')"
            @keydown.esc="cancelEdit"
            ref="titleInput"
            class="text-lg font-semibold flex-1"
          />
          <h1
            v-else
            @click="can.editBugs() && startEdit('title', bug?.title)"
            :class="can.editBugs() ? 'cursor-pointer hover:text-primary-600 hover:bg-gray-50 dark:hover:bg-gray-700 rounded px-2 -mx-2' : ''"
            class="text-lg font-semibold text-gray-900 dark:text-white truncate"
          >{{ bug?.title || 'Loading...' }}</h1>
          
          <!-- Deleted Badge -->
          <HLBadge v-if="bug?.is_deleted" id="bug-deleted-badge" color="gray" size="sm" class="flex-shrink-0">Deleted</HLBadge>
        </div>
        
        <!-- Header Controls -->
        <div class="flex items-center space-x-3 flex-shrink-0 ml-4">
          <!-- Reporter -->
          <div class="flex items-center text-xs text-gray-500 dark:text-gray-400 border-r border-gray-300 dark:border-gray-600 pr-3">
            <span class="mr-1.5">Reporter</span>
            <svg class="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            <span class="font-medium">{{ reporterName }}</span>
          </div>
          
          <!-- Created Date -->
          <div class="flex items-center text-xs text-gray-500 dark:text-gray-400">
            <span class="mr-1.5">Created</span>
            <svg class="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span>{{ formatDateOnly(bug?.created_at) }}</span>
          </div>
          
          <!-- Clone Button -->
          <HLButton
            v-if="!bug?.is_deleted && can.createBugs()"
            @click="cloneBug"
            variant="primary"
            size="sm"
            title="Clone this bug"
          >
            <HLIcon size="16" class="mr-1.5">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </HLIcon>
            Clone
          </HLButton>
          
          <!-- Delete/Restore Button -->
          <HLButton
            v-if="bug?.is_deleted && can.deleteBugs()"
            @click="restoreBug"
            variant="success"
            size="sm"
          >
            <HLIcon size="16" class="mr-1.5">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </HLIcon>
            Restore
          </HLButton>
          <HLButton
            v-else-if="!bug?.is_deleted && can.deleteBugs()"
            @click="deleteBug"
            variant="danger"
            size="sm"
          >
            <HLIcon size="16" class="mr-1.5">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </HLIcon>
            Delete
          </HLButton>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading && !bug" class="flex flex-col items-center justify-center py-12">
      <HLSpin id="bug-detail-loading-spinner" size="lg" :show="loading && !bug">
        <template #description>
          <HLText variant="body-sm" color="muted">Loading bug...</HLText>
        </template>
      </HLSpin>
    </div>

    <!-- Bug Content -->
    <div v-else-if="bug" class="flex-1 overflow-hidden px-5 mt-4">
      <!-- Main Content Grid -->
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-4 h-full pb-4">
        <!-- Left Column - Main Info -->
        <div class="lg:col-span-2 space-y-4 overflow-y-auto scrollbar-thin scrollbar-thumb-transparent hover:scrollbar-thumb-gray-300 dark:hover:scrollbar-thumb-gray-600 scrollbar-track-transparent">
          <!-- Bug Details -->
          <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
            <div class="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-5">
              <!-- Row 1: Status | Priority | Severity -->
              <div class="flex items-center">
                <svg class="w-4 h-4 mr-2.5 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span class="text-sm text-gray-600 dark:text-gray-400 w-20 flex-shrink-0 mr-3">Status</span>
                <HLSelect
                  id="bug-status-select"
                  v-model:value="editForm.status"
                  :options="statusOptions"
                  @update:value="handleFieldChange('status', $event)"
                  :disabled="!can.editBugs()"
                  size="sm"
                  class="flex-1"
                />
              </div>

              <div class="flex items-center">
                <svg class="w-4 h-4 mr-2.5 text-orange-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <span class="text-sm text-gray-600 dark:text-gray-400 w-20 flex-shrink-0 mr-3">Priority</span>
                <HLSelect
                  id="bug-priority-select"
                  v-model:value="editForm.priority"
                  :options="priorityOptions"
                  @update:value="handleFieldChange('priority', $event)"
                  :disabled="!can.editBugs()"
                  size="sm"
                  class="flex-1"
                />
              </div>

              <div class="flex items-center">
                <svg class="w-4 h-4 mr-2.5 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span class="text-sm text-gray-600 dark:text-gray-400 w-20 flex-shrink-0 mr-3">Severity</span>
                <HLSelect
                  id="bug-severity-select"
                  v-model:value="editForm.severity"
                  :options="severityOptions"
                  @update:value="handleFieldChange('severity', $event)"
                  :disabled="!can.editBugs()"
                  size="sm"
                  class="flex-1"
                />
              </div>

              <!-- Row 2: Type | Assigned To | Environment -->
              <div class="flex items-center">
                <svg class="w-4 h-4 mr-2.5 text-purple-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
                <span class="text-sm text-gray-600 dark:text-gray-400 w-20 flex-shrink-0 mr-3">Type</span>
                <HLSelect
                  id="bug-type-select"
                  v-model:value="editForm.type"
                  :options="typeOptions"
                  @update:value="handleFieldChange('type', $event)"
                  :disabled="!can.editBugs()"
                  size="sm"
                  class="flex-1"
                />
              </div>

              <div class="flex items-center">
                <svg class="w-4 h-4 mr-2.5 text-indigo-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <span class="text-sm text-gray-600 dark:text-gray-400 w-20 flex-shrink-0 mr-3">Assigned To</span>
                <HLSelect
                  id="bug-assigned-to-select"
                  v-model:value="editForm.assigned_to"
                  :options="assigneeOptions"
                  @update:value="handleFieldChange('assigned_to', $event)"
                  :disabled="!can.editBugs()"
                  size="sm"
                  filterable
                  clearable
                  class="flex-1"
                />
              </div>

              <div class="flex items-center">
                <svg class="w-4 h-4 mr-2.5 text-teal-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span class="text-sm text-gray-600 dark:text-gray-400 w-20 flex-shrink-0 mr-3">Environment</span>
                <HLSelect
                  id="bug-environment-select"
                  v-model:value="editForm.environment"
                  :options="environmentOptions"
                  @update:value="handleFieldChange('environment', $event)"
                  :disabled="!can.editBugs()"
                  size="sm"
                  class="flex-1"
                />
              </div>
            </div>
          </div>

          <!-- Steps to Reproduce -->
          <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
            <h3 class="text-sm font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
              <svg class="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
              Steps to Reproduce
            </h3>
            <div v-if="editingField === 'steps_to_reproduce'" class="w-full">
              <HLInput
                ref="stepsInput"
                v-model="editForm.steps_to_reproduce"
                type="textarea"
                :rows="5"
                placeholder="Enter steps to reproduce..."
                @blur="saveEdit('steps_to_reproduce')"
                @keydown.esc="cancelEdit"
                size="sm"
                class="w-full"
              />
            </div>
            <div 
              v-else
              @click="can.editBugs() && startEdit('steps_to_reproduce', bug?.steps_to_reproduce || [])"
              :class="[
                can.editBugs() ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700' : '',
                !bug?.steps_to_reproduce ? 'text-gray-400 dark:text-gray-500 italic h-12' : 'text-gray-700 dark:text-gray-300 max-h-40'
              ]"
              class="text-sm whitespace-pre-wrap overflow-y-auto rounded p-2 -m-2"
            >
              <div v-if="bug?.steps_to_reproduce" class="space-y-2">
                <div
                  v-for="(step, index) in stepsToReproduce"
                  :key="index"
                  class="flex items-start"
                >
                  <p class="text-gray-700 dark:text-gray-300">{{ step }}</p>
                </div>
              </div>
              <span v-else>No steps to reproduce specified</span>
            </div>
          </div>

          <!-- Expected vs Actual Result -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
              <h3 class="text-sm font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
                <svg class="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Expected Result
              </h3>
              <div v-if="editingField === 'expected_result'" class="w-full">
                <HLInput
                  ref="expectedResultInput"
                  v-model="editForm.expected_result"
                  type="textarea"
                  :rows="5"
                  placeholder="Enter expected result..."
                  @blur="saveEdit('expected_result')"
                  @keydown.esc="cancelEdit"
                  size="sm"
                  class="w-full"
                />
              </div>
              <div 
                v-else
                @click="can.editBugs() && startEdit('expected_result', bug?.expected_result || '')"
                :class="[
                  can.editBugs() ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700' : '',
                  !bug?.expected_result ? 'text-gray-400 dark:text-gray-500 italic h-12' : 'text-gray-700 dark:text-gray-300 max-h-40'
                ]"
                class="text-sm whitespace-pre-wrap overflow-y-auto rounded p-2 -m-2"
              >
                {{ bug?.expected_result || 'No expected result specified' }}
              </div>
            </div>

            <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
              <h3 class="text-sm font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
                <svg class="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
                Actual Result
              </h3>
              <div v-if="editingField === 'actual_result'" class="w-full">
                <HLInput
                  ref="actualResultInput"
                  v-model="editForm.actual_result"
                  type="textarea"
                  :rows="5"
                  placeholder="Enter actual result..."
                  @blur="saveEdit('actual_result')"
                  @keydown.esc="cancelEdit"
                  size="sm"
                  class="w-full"
                />
              </div>
              <div 
                v-else
                @click="can.editBugs() && startEdit('actual_result', bug?.actual_result || '')"
                :class="[
                  can.editBugs() ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700' : '',
                  !bug?.actual_result ? 'text-gray-400 dark:text-gray-500 italic h-12' : 'text-gray-700 dark:text-gray-300 max-h-40'
                ]"
                class="text-sm whitespace-pre-wrap overflow-y-auto rounded p-2 -m-2"
              >
                {{ bug?.actual_result || 'No actual result specified' }}
              </div>
            </div>
          </div>

          <!-- Test Data -->
          <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
            <h3 class="text-sm font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
              <svg class="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
              </svg>
              Test Data
            </h3>
            <div v-if="editingField === 'test_data'" class="w-full">
              <HLInput
                ref="testDataInput"
                v-model="editForm.test_data"
                type="textarea"
                :rows="5"
                placeholder="Enter test data..."
                @blur="saveEdit('test_data')"
                @keydown.esc="cancelEdit"
                size="sm"
                class="w-full"
              />
            </div>
            <div 
              v-else
              @click="can.editBugs() && startEdit('test_data', bug?.test_data || '')"
              :class="[
                can.editBugs() ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700' : '',
                !bug?.test_data ? 'text-gray-400 dark:text-gray-500 italic h-12' : 'text-gray-700 dark:text-gray-300 max-h-40'
              ]"
              class="text-sm whitespace-pre-wrap overflow-y-auto rounded p-2 -m-2"
            >
              {{ bug?.test_data || 'No test data specified' }}
            </div>
          </div>

          <!-- Attachments -->
          <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
            <h3 class="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
              <svg class="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
              </svg>
              Attachments
            </h3>
            
            <!-- Upload Component (only show if user can edit) -->
            <div v-if="can.editBugs()" class="mb-4 upload-container">
              <HLUpload
                v-model:file-list="attachmentFileList"
                :max="5"
                :disabled="uploadingAttachments"
                multiple
                accept="image/*"
                type="draggable"
                @change="handleAttachmentChange"
                @remove="handleAttachmentRemove"
              >
                <template #buttonContent>
                  <HLText variant="body-sm">Upload images (max 5, 4MB each)</HLText>
                </template>
              </HLUpload>
            </div>

            <!-- Display uploaded attachments -->
            <div v-if="attachmentLinks.length > 0" class="flex flex-row gap-2 mt-4 overflow-x-auto pb-2">
              <div
                v-for="(link, index) in attachmentLinks"
                :key="index"
                class="relative group flex-shrink-0"
              >
                <a
                  :href="link"
                  target="_blank"
                  @click.prevent="openModalForAttachment(link)"
                  rel="noopener noreferrer"
                  class="block w-20 h-20 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 hover:border-blue-500 dark:hover:border-blue-400 transition-colors cursor-pointer"
                >
                  <img
                    :src="link"
                    :alt="`Attachment ${index + 1}`"
                    class="w-full h-full object-cover"
                    @error="handleImageError"
                  />
                </a>
                <button
                  v-if="can.editBugs()"
                  @click.stop="removeAttachment(link)"
                  class="absolute -top-1 -right-1 opacity-0 group-hover:opacity-100 bg-red-500 text-white rounded-full p-0.5 transition-opacity z-10"
                  title="Remove attachment"
                >
                  <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div v-else class="text-sm text-gray-400 dark:text-gray-500 italic py-4 text-center">
              No attachments
            </div>
          </div>

          <!-- Additional Info Tabs -->
          <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            <div class="px-6 pt-4">
              <HLTabs 
                :key="tabsKey"
                :value="activeTab" 
                @update:value="activeTab = $event"
                :animated="false" 
                size="lg"
                :maxVisibleTabs="additionalTabs.length"
              >
                <HLTab v-for="tab in additionalTabs" :key="tab.id" :name="tab.id">
                  <HLIcon size="18" class="mr-1.5">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" vector-effect="non-scaling-stroke" shape-rendering="geometricPrecision" :style="{ color: tab.iconColorHex }" aria-hidden="true">
                      <path :d="tab.svgPath" />
                    </svg>
                  </HLIcon>
                  {{ tab.name }}
                  <span v-if="tab.count !== undefined" class="ml-2 text-xs bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded-full">
                    {{ tab.count }}
                  </span>
                </HLTab>
              </HLTabs>
            </div>

            <div class="p-4">
              <!-- Related Features Tab -->
              <div v-if="activeTab === 'features'">
                <!-- Link Feature Section -->
                <div v-if="can.editBugs()" class="mb-4 p-4 bg-blue-50 dark:bg-blue-900/10 rounded-lg border border-blue-200 dark:border-blue-800">
                  <div class="flex items-center space-x-3">
                    <div class="flex-1 relative">
                      <HLInput
                        v-model="featureSearchQuery"
                        type="text"
                        placeholder="Search or select a feature to link..."
                        class="w-full"
                        @click="showFeatureDropdown = !showFeatureDropdown"
                        @input="showFeatureDropdown = true"
                        size="sm"
                      />
                      <!-- Dropdown -->
                      <div 
                        v-if="showFeatureDropdown && filteredAvailableFeatures.length > 0" 
                        class="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto"
                      >
                        <div
                          v-for="feature in filteredAvailableFeatures.slice(0, 20)"
                          :key="feature.id"
                          @mousedown.prevent="selectFeatureToLink(feature.id)"
                          class="px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 border-b border-gray-200 dark:border-gray-700 last:border-b-0"
                        >
                          <span class="font-medium text-blue-600 dark:text-blue-400">{{ feature.featureId }}</span>
                          <span class="text-gray-700 dark:text-gray-300"> - {{ feature.title }}</span>
                        </div>
                      </div>
                    </div>
                    <HLButton
                      @click="linkSelectedFeature"
                      :disabled="!selectedFeatureId"
                      color="blue"
                      variant="primary"
                      size="md"
                    >
                      <template #iconLeft>
                        <Icon name="link" size="sm" />
                      </template>
                      Link
                    </HLButton>
                    <HLButton
                      @click="refreshFeatures"
                      color="gray"
                      variant="secondary"
                      size="md"
                      title="Refresh features list"
                    >
                      <template #iconLeft>
                        <Icon name="refresh" size="sm" />
                      </template>
                    </HLButton>
                  </div>
                </div>

                <div v-if="relatedFeatures.length === 0" class="text-center py-4">
                  <p class="text-sm text-gray-500 dark:text-gray-400">No features linked yet</p>
                </div>
                <div v-else class="overflow-x-auto">
                  <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead class="bg-gray-50 dark:bg-gray-900">
                      <tr>
                        <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Feature ID</th>
                        <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Title</th>
                        <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                        <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Priority</th>
                        <th v-if="can.editBugs()" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      <tr
                        v-for="feature in relatedFeatures"
                        :key="feature.id"
                        class="hover:bg-gray-50 dark:hover:bg-gray-700"
                      >
                        <td class="px-6 py-4 whitespace-nowrap cursor-pointer" @click="viewFeature(feature.id)">
                          <span class="text-sm font-medium text-primary-600 dark:text-primary-400">{{ feature.featureId }}</span>
                        </td>
                        <td class="px-6 py-4 cursor-pointer" @click="viewFeature(feature.id)">
                          <span class="text-sm text-gray-900 dark:text-white">{{ feature.title }}</span>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap cursor-pointer" @click="viewFeature(feature.id)">
                          <HLBadge :id="`feature-status-${feature.id}`" :color="getFeatureStatusVariant(feature.status)" size="sm">{{ getFeatureStatusLabel(feature.status) }}</HLBadge>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap cursor-pointer" @click="viewFeature(feature.id)">
                          <HLBadge :id="`feature-priority-${feature.id}`" :color="getFeaturePriorityVariant(feature.priority)" size="sm">{{ getFeaturePriorityLabel(feature.priority) }}</HLBadge>
                        </td>
                        <td v-if="can.editBugs()" class="px-6 py-4 whitespace-nowrap" @click.stop>
                          <HLButton
                            @click="unlinkFeature(feature.id)"
                            color="red"
                            variant="text"
                            size="sm"
                            title="Unlink feature"
                          >
                            <template #iconLeft>
                              <Icon name="close" size="sm" />
                            </template>
                          </HLButton>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Linked Test Cases Tab -->
              <div v-if="activeTab === 'testCases'">
                <!-- Link Test Case Section -->
                <div v-if="can.editBugs()" class="mb-4 p-4 bg-green-50 dark:bg-green-900/10 rounded-lg border border-green-200 dark:border-green-800">
                  <div class="flex items-center space-x-3">
                    <div class="flex-1 relative">
                      <HLInput
                        v-model="testCaseSearchQuery"
                        type="text"
                        placeholder="Search or select a test case to link..."
                        class="w-full"
                        @click="showTestCaseDropdown = !showTestCaseDropdown"
                        @input="showTestCaseDropdown = true"
                        size="sm"
                      />
                      <!-- Dropdown -->
                      <div 
                        v-if="showTestCaseDropdown && filteredAvailableTestCases.length > 0" 
                        class="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto"
                      >
                        <div
                          v-for="testCase in filteredAvailableTestCases.slice(0, 20)"
                          :key="testCase.id"
                          @mousedown.prevent="selectTestCaseToLink(testCase.id)"
                          class="px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 border-b border-gray-200 dark:border-gray-700 last:border-b-0"
                        >
                          <span class="font-medium text-green-600 dark:text-green-400">{{ testCase.testCaseId }}</span>
                          <span class="text-gray-700 dark:text-gray-300"> - {{ testCase.name }}</span>
                        </div>
                      </div>
                    </div>
                    <HLButton
                      @click="linkSelectedTestCase"
                      :disabled="!selectedTestCaseId"
                      color="blue"
                      variant="primary"
                      size="md"
                    >
                      <template #iconLeft>
                        <Icon name="link" size="sm" />
                      </template>
                      Link
                    </HLButton>
                    <HLButton
                      @click="refreshTestCases"
                      color="gray"
                      variant="secondary"
                      size="md"
                      title="Refresh test cases list"
                    >
                      <template #iconLeft>
                        <Icon name="refresh" size="sm" />
                      </template>
                    </HLButton>
                  </div>
                </div>

                <div v-if="linkedTestCases.length === 0" class="text-center py-4">
                  <p class="text-sm text-gray-500 dark:text-gray-400">No test cases linked yet</p>
                </div>
                <div v-else class="overflow-x-auto">
                  <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead class="bg-gray-50 dark:bg-gray-900">
                      <tr>
                        <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Test Case ID</th>
                        <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Name</th>
                        <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                        <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Priority</th>
                        <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Assignee</th>
                        <th v-if="can.editBugs()" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      <tr
                        v-for="testCase in linkedTestCases"
                        :key="testCase.id"
                        class="hover:bg-gray-50 dark:hover:bg-gray-700"
                      >
                        <td class="px-6 py-4 whitespace-nowrap cursor-pointer" @click="viewTestCase(testCase.id)">
                          <span class="text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline">{{ testCase.testCaseId }}</span>
                        </td>
                        <td class="px-6 py-4 cursor-pointer" @click="viewTestCase(testCase.id)">
                          <span class="text-sm text-gray-900 dark:text-white">{{ testCase.name }}</span>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap cursor-pointer" @click="viewTestCase(testCase.id)">
                          <HLBadge :id="`testcase-status-${testCase.id}`" :color="getTestCaseStatusVariant(testCase.status)" size="sm">{{ getTestCaseStatusLabel(testCase.status) }}</HLBadge>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap cursor-pointer" @click="viewTestCase(testCase.id)">
                          <HLBadge :id="`testcase-priority-${testCase.id}`" :color="getTestCasePriorityVariant(testCase.priority)" size="sm">{{ getTestCasePriorityLabel(testCase.priority) }}</HLBadge>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap cursor-pointer" @click="viewTestCase(testCase.id)">
                          <span class="text-sm text-gray-900 dark:text-white">{{ getTestCaseAssigneeName(testCase.assigneeId || testCase.assignee_id) }}</span>
                        </td>
                        <td v-if="can.editBugs()" class="px-6 py-4 whitespace-nowrap" @click.stop>
                          <HLButton
                            @click="unlinkTestCase(testCase.id)"
                            color="red"
                            variant="text"
                            size="sm"
                            :disabled="linkedTestCases.length === 1"
                            :class="linkedTestCases.length === 1 ? 'opacity-50 cursor-not-allowed' : ''"
                            :title="linkedTestCases.length === 1 ? 'Cannot unlink the last test case' : 'Unlink test case'"
                          >
                            <HLIcon size="16">
                              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </HLIcon>
                          </HLButton>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Test Runs Tab -->
              <div v-if="activeTab === 'testRuns'">
                <div v-if="linkedTestRuns.length === 0" class="text-center py-4">
                  <p class="text-sm text-gray-500 dark:text-gray-400">No test runs linked yet</p>
                </div>
                <div v-else class="overflow-x-auto">
                  <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead class="bg-gray-50 dark:bg-gray-900">
                      <tr>
                        <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Name</th>
                        <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                        <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Environment</th>
                        <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Test Cases</th>
                      </tr>
                    </thead>
                    <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      <tr
                        v-for="testRun in linkedTestRuns"
                        :key="testRun.id"
                        class="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                        @click="viewTestRun(testRun.id)"
                      >
                        <td class="px-6 py-4">
                          <span class="text-sm font-medium text-gray-900 dark:text-white">{{ testRun.name }}</span>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap">
                          <HLBadge :id="`testrun-status-${testRun.id}`" :color="getTestRunStatusVariant(testRun.status)" size="sm">
                            {{ formatTestRunStatus(testRun.status) }}
                          </HLBadge>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap">
                          <span class="text-sm text-gray-900 dark:text-white">{{ testRun.environment ? formatEnvironment(testRun.environment) : 'N/A' }}</span>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap">
                          <span class="text-sm text-gray-900 dark:text-white">{{ testRun.test_cases ? testRun.test_cases.length : 0 }}</span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

            </div>
          </div>
        </div>

        <!-- Right Column - Comments & Activity Sidebar -->
        <div class="h-full overflow-hidden flex flex-col">
          <!-- Comments & Activity Tabs -->
          <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 flex flex-col flex-1 min-h-0">
            <div class="px-6 pt-4">
              <HLTabs 
                :key="`sidebar-tabs-${comments.length}-${activityCount}`"
                :value="activeSidebarTab" 
                @update:value="activeSidebarTab = $event"
                :animated="false" 
                size="lg"
                :maxVisibleTabs="sidebarTabs.length"
              >
                <HLTab v-for="tab in sidebarTabs" :key="`${tab.id}-${tab.count}`" :name="tab.id">
                  <HLIcon size="18" class="mr-1.5">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" vector-effect="non-scaling-stroke" shape-rendering="geometricPrecision" :style="{ color: tab.iconColorHex }" aria-hidden="true">
                      <path :d="tab.svgPath" />
                    </svg>
                  </HLIcon>
                  {{ tab.name }}
                  <span v-if="tab.count !== undefined" class="ml-2 text-xs bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded-full">
                    {{ tab.count }}
                  </span>
                </HLTab>
              </HLTabs>
            </div>

            <div class="p-4 flex-1 flex flex-col min-h-0">
              <!-- Comments Tab -->
              <div v-if="activeSidebarTab === 'comments'" class="flex flex-col h-full min-h-0">
                <!-- Comments List -->
                <div v-if="comments.length > 0" class="space-y-2 flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-transparent hover:scrollbar-thumb-gray-300 dark:hover:scrollbar-thumb-gray-600 scrollbar-track-transparent pr-2 min-h-0">
                  <div
                    v-for="comment in comments"
                    :key="comment.id"
                    class="group"
                  >
                    <!-- Chat-style comment bubble -->
                    <div class="flex items-start space-x-3">
                      <!-- Avatar -->
                      <div class="flex-shrink-0">
                        <div class="w-8 h-8 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white font-semibold text-sm">
                          {{ comment.user_name ? comment.user_name.charAt(0).toUpperCase() : 'U' }}
                        </div>
                      </div>
                      
                      <!-- Comment content -->
                      <div class="flex-1 min-w-0">
                        <div class="flex items-center space-x-2 mb-1">
                          <span class="font-semibold text-sm text-gray-900 dark:text-white">{{ comment.user_name }}</span>
                          <span 
                            class="text-xs text-gray-500 dark:text-gray-400 cursor-default"
                            :title="formatFullDateTime(comment.timestamp || comment.created_at)"
                          >
                            {{ formatDateTime(comment.timestamp || comment.created_at) }}
                          </span>
                          <span v-if="comment.is_edited" class="text-xs text-gray-400 dark:text-gray-500 italic">edited</span>
                        </div>
                        
                        <!-- Normal comment view -->
                        <div v-if="editingCommentId !== comment.id" class="bg-gray-50 dark:bg-gray-700 rounded-lg rounded-tl-none px-4 py-2.5 relative group/comment">
                          <p class="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">{{ comment.comment }}</p>
                          
                          <!-- Action buttons (visible on hover) -->
                          <div v-if="comment.user_id === authStore.user?.id" class="absolute top-0 right-0 -mt-2 -mr-2 opacity-0 group-hover/comment:opacity-100 transition-opacity flex space-x-1">
                            <HLButton
                              @click="startEditComment(comment)"
                              variant="ghost"
                              size="sm"
                              class="p-1.5 rounded-full shadow-md"
                              title="Edit comment"
                            >
                              <HLIcon size="14">
                                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              </HLIcon>
                            </HLButton>
                            <HLButton
                              @click="deleteComment(comment)"
                              variant="danger"
                              size="sm"
                              class="p-1.5 rounded-full shadow-md"
                              title="Delete comment"
                            >
                              <HLIcon size="14">
                                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </HLIcon>
                            </HLButton>
                          </div>
                        </div>
                        
                        <!-- Edit mode -->
                        <div v-else class="bg-white dark:bg-gray-700 rounded-lg rounded-tl-none border-2 border-primary-500 p-2">
                          <div class="relative">
                            <HLInput
                              v-model="editingCommentText"
                              type="textarea"
                              :rows="3"
                              class="w-full pr-24"
                              @keydown.esc="cancelEditComment"
                              size="sm"
                            />
                            <div class="absolute top-1/2 -translate-y-1/2 right-2 flex items-center space-x-1">
                              <HLButton
                                @click="cancelEditComment"
                                variant="ghost"
                                size="sm"
                                class="p-1.5"
                                title="Cancel"
                              >
                                <HLIcon size="16">
                                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </HLIcon>
                              </HLButton>
                              <HLButton
                                @click="saveEditComment(comment)"
                                :disabled="!editingCommentText.trim() || editingCommentText === comment.comment"
                                variant="primary"
                                size="sm"
                                class="p-1.5"
                                title="Save changes"
                              >
                                <HLIcon size="16">
                                  <svg class="rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                                  </svg>
                                </HLIcon>
                              </HLButton>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div v-else class="text-gray-500 dark:text-gray-400 text-center py-8 flex-1 flex items-center justify-center">
                  <div class="text-center">
                    <svg class="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    <p class="text-sm">No comments yet. Be the first to comment!</p>
                  </div>
                </div>

                <!-- Add Comment - Fixed at bottom -->
                <div class="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
                  <div class="relative">
                    <HLInput
                      v-model="newComment"
                      type="textarea"
                      :rows="3"
                      placeholder="Write a comment... (Ctrl+Enter or Shift+Enter to send)"
                      class="w-full pr-12"
                      size="sm"
                      @keydown.ctrl.enter="addComment"
                      @keydown.shift.enter="addComment"
                    />
                    <HLButton
                      @click="addComment"
                      :disabled="!newComment.trim()"
                      color="blue"
                      variant="primary"
                      size="sm"
                      class="absolute top-1/2 -translate-y-1/2 right-2 p-2"
                      title="Send comment"
                    >
                      <HLIcon size="20">
                        <svg class="rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                        </svg>
                      </HLIcon>
                    </HLButton>
                  </div>
                </div>
              </div>

              <!-- Activity Tab -->
              <div v-if="activeSidebarTab === 'activity'" class="flex flex-col h-full min-h-0">
                <div v-if="activities.length > 0" class="space-y-2 flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-transparent hover:scrollbar-thumb-gray-300 dark:hover:scrollbar-thumb-gray-600 scrollbar-track-transparent pr-2 pl-4 min-h-0">
                  <div
                    v-for="(activity, index) in activities"
                    :key="index"
                    class="relative pl-8 pb-2 ml-1 border-l-2 border-gray-200 dark:border-gray-700 last:border-transparent"
                  >
                    <!-- Activity Icon -->
                    <div class="absolute -left-4 top-0">
                      <div class="w-7 h-7 rounded-full flex items-center justify-center border-2 border-white dark:border-gray-800"
                        :class="getActivityIconClass(activity.type)"
                      >
                        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path v-if="activity.type === 'created'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          <path v-else-if="activity.type === 'updated'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          <path v-else-if="activity.type === 'linked'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                          <path v-else-if="activity.type === 'deleted'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          <path v-else-if="activity.type === 'restored'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                    </div>
                    
                    <!-- Activity Content -->
                    <div class="ml-2">
                      <div class="flex items-start justify-between gap-2">
                        <p class="text-sm text-gray-900 dark:text-white flex-1">
                          <span class="font-semibold">{{ getActivityUserName(activity) }}</span> <span class="text-gray-600 dark:text-gray-400">{{ formatActivityDescription(activity) }}</span>
                        </p>
                      </div>
                      <div class="flex items-center gap-2 mt-1">
                        <p 
                          class="text-xs text-gray-500 dark:text-gray-400 cursor-default"
                          :title="formatFullDateTime(activity.timestamp)"
                        >
                          {{ formatDateTime(activity.timestamp) }}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div v-else class="text-gray-500 dark:text-gray-400 text-center py-4 flex-1 flex items-center justify-center">
                  <div class="text-center">
                    <svg class="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    <p class="text-sm">No activities yet</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Not Found -->
    <div v-else class="flex items-center justify-center min-h-screen">
      <div class="text-center">
        <p class="text-gray-500 dark:text-gray-400">Bug not found</p>
        <HLButton
          @click="goBack"
          variant="primary"
          size="sm"
          class="mt-4"
        >
          Go Back
        </HLButton>
      </div>
    </div>
  </div>

  <!-- Image Preview Modal -->
  <HLModal
    id="image-preview-modal"
    type="default"
    :width="1000"
    v-model:show="imagePreviewModalOpen"
    :maskClosable="true"
    :closeOnEsc="true"
    :showFooter="true"
    @close="closeImagePreview"
  >
    <template #header>
      <div class="flex items-center justify-between w-full">
        <span class="text-sm text-gray-500 dark:text-gray-400">
          Image {{ currentImageIndex + 1 }} of {{ attachmentLinks.length }}
        </span>
      </div>
    </template>

    <div class="image-preview-container bg-gray-900 p-4">
      <HLCarousel
        :carouselItems="carouselItems"
        :showArrow="true"
        :showDots="attachmentLinks.length > 1"
        id="image-preview-carousel"
        :currentIndex="currentImageIndex"
        @update:index="onCarouselIndexUpdate"
        effect="fade"
        class="w-full h-full"
      />
    </div>

    <!-- Image Info Footer -->
    <template #footer>
      <div class="p-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <div class="flex items-center justify-center">
          <a
            :href="currentImageUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="text-sm text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
            Open in new tab
          </a>
        </div>
      </div>
    </template>
  </HLModal>
</template>

<script setup>
import { ref, computed, onMounted, nextTick, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { bugService, userService, testCaseService } from '@/database/services'
import { usePermissions } from '@/composables/usePermissions'
import { useToast } from '@/composables/useToast'
import { usePageTitle, formatDetailTitle } from '@/composables/usePageTitle'
import { useAuthStore } from '@/stores/auth'
import { useBugStore } from '@/stores/bug'
import { HLBadge, HLSpace, HLButton, HLTabs, HLTab, HLIcon, HLSelect, HLInput, HLSpin, HLText, HLUpload, HLModal, HLCarousel } from '@platform-ui/highrise'
import Icon from '@/components/ui/Icon.vue'
import EmptyState from '@/components/ui/EmptyState.vue'
import imageCompression from 'browser-image-compression'
import {
  BUG_STATUS_OPTIONS,
  BUG_PRIORITY_OPTIONS,
  BUG_TYPE_OPTIONS,
  BUG_TYPE,
  BUG_SEVERITY_OPTIONS,
  ENVIRONMENT_OPTIONS
} from '@/constants/bugs'
import { getIconPath } from '@/constants/icons'
import { TAB_ICON_COLORS } from '@/constants/projects'
import { getStatusColor as getStatusColorFromConstants } from '@/constants/statusColors'
import { getBugStatusBadge } from '@/utils/badgeHelpers'

// Route & Router
const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const bugStore = useBugStore()
const { can } = usePermissions()
const { success: showSuccess, error: showError } = useToast()
const { setTitle } = usePageTitle()

// Reactive data
const loading = ref(false)
const bug = ref(null)
const users = ref([])
const linkedTestCases = ref([])
const linkedTestRuns = ref([])
const relatedFeatures = ref([])
const comments = ref([])
const newComment = ref('')
const addingComment = ref(false)
const loadingComments = ref(false)
const editingCommentId = ref(null)
const editingCommentText = ref('')
const activities = ref([])
const activeTab = ref('features')
const activeSidebarTab = ref('comments')
const tabsKey = ref(0)  // Force re-render of tabs when data changes

// Feature linking state
const availableFeatures = ref([])
const selectedFeatureId = ref(null)
const featureSearchQuery = ref('')
const showFeatureDropdown = ref(false)

// Test case linking state
const availableTestCases = ref([])
const selectedTestCaseId = ref(null)
const testCaseSearchQuery = ref('')
const showTestCaseDropdown = ref(false)

// Inline editing state
const editingField = ref(null)
const editForm = ref({
  title: '',
  status: '',
  priority: '',
  severity: '',
  type: '',
  assigned_to: '',
  environment: '',
  steps_to_reproduce: '',
  expected_result: '',
  actual_result: '',
  test_data: '',
  attachments: ''
})
const titleInput = ref(null)
const stepsInput = ref(null)
const expectedResultInput = ref(null)
const actualResultInput = ref(null)
const testDataInput = ref(null)
const attachmentsInput = ref(null)
const uploadingAttachments = ref(false)
const attachmentFileList = ref([])
const uploadDebounceTimer = ref(null)
const pendingFiles = ref([])
const isCompressing = ref(false)

// Image preview modal state
const imagePreviewModalOpen = ref(false)
const currentImageUrl = ref('')
const currentImageIndex = ref(-1)

// Computed
const projectId = computed(() => route.params.projectId)
const bugId = computed(() => route.params.bugId)

const stepsToReproduce = computed(() => {
  if (!bug.value?.steps_to_reproduce) return []
  // Handle both array (new format) and string (old format) for backward compatibility
  if (Array.isArray(bug.value.steps_to_reproduce)) {
    return bug.value.steps_to_reproduce.filter(step => step && step.trim && step.trim().length > 0)
  }
  return bug.value.steps_to_reproduce.split('\n').filter(step => step.trim().length > 0)
})

const attachmentLinks = computed(() => {
  if (!bug.value?.attachments) return []
  // Handle both array (new format) and string (old format) for backward compatibility
  if (Array.isArray(bug.value.attachments)) {
    return bug.value.attachments.filter(link => link && link.trim && link.trim().length > 0)
  }
  return bug.value.attachments.split('\n').filter(link => link.trim().length > 0)
})

const carouselItems = computed(() => {
  return attachmentLinks.value.map((url, index) => ({
    key: `attachment-${index}`,
    src: url,
    alt: `Attachment ${index + 1}`
  }))
})

const reporterName = computed(() => {
  if (!bug.value?.created_by) return 'Unknown'
  const user = users.value.find(u => u.id === bug.value.created_by)
  return user ? (user.name || user.email) : 'Unknown'
})

// Computed properties for bug ID badge colors based on status
// Use centralized utility for bug ID badge styling
const bugIdBadgeClasses = computed(() => {
  if (!bug.value?.status) {
    // Default red color if no status
    return 'bg-red-100 dark:bg-red-900/30 border-red-200 dark:border-red-700'
  }
  
  // Use centralized utility function from badgeHelpers
  const badgeConfig = getBugStatusBadge(bug.value.status)
  // Extract background and add border styling
  const bgClasses = badgeConfig.class || 'bg-red-100 dark:bg-red-900/30'
  
  // Derive border color from background color
  const borderColor = bgClasses.includes('red') ? 'border-red-200 dark:border-red-700'
    : bgClasses.includes('green') ? 'border-green-200 dark:border-green-700'
    : bgClasses.includes('blue') ? 'border-blue-200 dark:border-blue-700'
    : bgClasses.includes('yellow') ? 'border-yellow-200 dark:border-yellow-700'
    : bgClasses.includes('gray') ? 'border-gray-200 dark:border-gray-700'
    : bgClasses.includes('purple') ? 'border-purple-200 dark:border-purple-700'
    : bgClasses.includes('orange') ? 'border-orange-200 dark:border-orange-700'
    : 'border-gray-200 dark:border-gray-700'
  
  return `${bgClasses.split(' ').filter(c => c.startsWith('bg-')).join(' ')} ${borderColor}`
})

const bugIdTextClasses = computed(() => {
  if (!bug.value?.status) {
    // Default red text if no status
    return 'text-red-600 dark:text-red-400'
  }
  
  // Use centralized utility function from badgeHelpers
  const badgeConfig = getBugStatusBadge(bug.value.status)
  // Extract text color from badge class
  const textClasses = badgeConfig.class?.split(' ').filter(c => c.startsWith('text-')).join(' ')
  return textClasses || 'text-red-600 dark:text-red-400'
})

const assigneeName = computed(() => {
  if (!bug.value?.assigned_to) return 'Unassigned'
  const user = users.value.find(u => u.id === bug.value.assigned_to)
  return user ? (user.name || user.email) : 'Unassigned'
})

// Computed options for HLSelect
const statusOptions = computed(() => BUG_STATUS_OPTIONS)
const priorityOptions = computed(() => BUG_PRIORITY_OPTIONS)
const severityOptions = computed(() => BUG_SEVERITY_OPTIONS)
const typeOptions = computed(() => BUG_TYPE_OPTIONS)
const environmentOptions = computed(() => ENVIRONMENT_OPTIONS)
const assigneeOptions = computed(() => {
  return [
    { value: '', label: 'Unassigned' },
    ...users.value.map(user => ({
      value: user.id,
      label: user.name || user.email
    }))
  ]
})

const additionalTabs = computed(() => {
  return [
    { 
      id: 'features', 
      name: 'Related Features', 
      count: relatedFeatures.value.length,
      svgPath: getIconPath('feature'),
      iconColorHex: TAB_ICON_COLORS.FEATURES
    },
    { 
      id: 'testCases', 
      name: 'Linked Test Cases', 
      count: linkedTestCases.value.length,
      svgPath: getIconPath('testCase'),
      iconColorHex: TAB_ICON_COLORS.TEST_CASES
    },
    { 
      id: 'testRuns', 
      name: 'Test Runs', 
      count: linkedTestRuns.value.length,
      svgPath: getIconPath('testRun'),
      iconColorHex: TAB_ICON_COLORS.TEST_RUNS
    }
  ]
})

// Computed property for activity count
const activityCount = computed(() => {
  return activities.value?.length ?? 0
})

const sidebarTabs = computed(() => [
  { 
    id: 'comments', 
    name: 'Comments', 
    count: comments.value.length,
    svgPath: getIconPath('comment'),
    iconColorHex: TAB_ICON_COLORS.COMMENTS
  },
  { 
    id: 'activity', 
    name: 'Activity', 
    count: activityCount.value,
    svgPath: getIconPath('clock'),
    iconColorHex: TAB_ICON_COLORS.ACTIVITY
  }
])

// Methods
const getActivityUserName = (activity) => {
  // First check if activity already has user_name
  if (activity?.user_name) return activity.user_name
  
  // Then try to get from user_id
  const userId = activity?.user_id
  if (!userId) return 'System'
  
  // Look up user by id or uid
  const user = users.value.find(u => u.id === userId || u.uid === userId)
  return user ? (user.name || user.email) : 'Unknown User'
}

const formatActivityDate = (date) => {
  if (!date) return ''
  const activityDate = new Date(date)
  const now = new Date()
  const diffMs = now - activityDate
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)
  
  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`
  if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`
  if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`
  
  return activityDate.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: activityDate.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
  })
}

const formatChangeValue = (value, fieldKey) => {
  if (value === null || value === undefined || value === '') return '(empty)'
  
  // Special handling for user IDs (assignee field)
  if (fieldKey === 'assigned_to' && typeof value === 'string') {
    const user = users.value.find(u => u.id === value)
    return user ? (user.name || user.email) : value
  }
  
  if (typeof value === 'string') return value
  return String(value)
}

// Format activity description, resolving user IDs to names for assignee changes
const formatActivityDescription = (activity) => {
  // If no description or empty string, try to generate one from details
  if (!activity.description || activity.description.trim() === '') {
    // If it's a field_updated type with details, generate description from details
    if (activity.type === 'field_updated' && activity.details) {
      const field = activity.details.field || 'a field'
      const oldValue = activity.details.oldValue || 'Not set'
      const newValue = activity.details.newValue || 'Not set'
      
      // Format field name
      const fieldName = field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
      
      // Format values
      const formatValue = (val) => {
        if (val === null || val === undefined || val === '') return 'Not set'
        if (typeof val === 'string') {
          // Capitalize and format enum values
          return val.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
        }
        return String(val)
      }
      
      return `${fieldName} changed from "${formatValue(oldValue)}" to "${formatValue(newValue)}"`
    }
    
    // Fallback based on activity type
    switch (activity.type) {
      case 'created':
        return 'created this bug'
      case 'updated':
        return 'updated this bug'
      case 'field_updated':
        return `updated ${activity.details?.field || 'a field'}`
      case 'deleted':
        return 'deleted this bug'
      case 'restored':
        return 'restored this bug'
      case 'linked':
        return 'linked this bug'
      case 'comment_added':
        return 'added a comment'
      default:
        return 'performed an action'
    }
  }
  
  // Check if this is an assigned_to field change
  if (activity.type === 'field_updated' && activity.details?.field === 'assigned_to') {
    // Extract old and new values from description or details
    const oldValue = activity.details?.oldValue
    const newValue = activity.details?.newValue
    
    // Helper to get user name from ID
    const getUserName = (userId) => {
      if (!userId || userId === '' || userId === 'Not set') return 'Unassigned'
      const user = users.value.find(u => u.id === userId || u.uid === userId)
      return user ? (user.name || user.email || 'Unknown User') : userId
    }
    
    const oldName = getUserName(oldValue)
    const newName = getUserName(newValue)
    
    return `Assigned To changed from "${oldName}" to "${newName}"`
  }
  
  // For other fields, check if description contains user IDs that look like assignee IDs
  // Pattern: "Assigned To changed from "user_xxx" to "user_yyy""
  const assigneePattern = /Assigned To changed from "([^"]+)" to "([^"]+)"/
  const match = activity.description.match(assigneePattern)
  if (match) {
    const oldUserId = match[1]
    const newUserId = match[2]
    
    // Helper to get user name from ID
    const getUserName = (userId) => {
      if (!userId || userId === '' || userId === 'Not set') return 'Unassigned'
      // Check if it's already a name (doesn't start with 'user_' or look like an ID)
      if (!userId.startsWith('user_') && !userId.match(/^[a-z0-9_]+$/i)) {
        return userId // Already a name
      }
      const user = users.value.find(u => u.id === userId || u.uid === userId)
      return user ? (user.name || user.email || 'Unknown User') : userId
    }
    
    const oldName = getUserName(oldUserId)
    const newName = getUserName(newUserId)
    
    return `Assigned To changed from "${oldName}" to "${newName}"`
  }
  
  // Return original description for other cases
  return activity.description
}

const loadBug = async () => {
  // Validate bugId before making API call
  if (!bugId.value) {
    console.warn('⚠️ Cannot load bug: bugId is undefined')
    loading.value = false
    return
  }
  
  try {
    loading.value = true
    const bugData = await bugService.getById(bugId.value)
    
    if (!bugData) {
      showError('Bug not found')
      goBack()
      return
    }

    bug.value = bugData

    // Update document title with bug ID
    const idToUse = bugData?.bugId || bugId.value
    if (idToUse) {
      setTitle(formatDetailTitle('Bug', idToUse, bugData?.title))
    }

    // Sync editForm with bug values for HLSelect components
    editForm.value = {
      title: bugData.title || '',
      status: bugData.status || '',
      priority: bugData.priority || '',
      severity: bugData.severity || '',
      type: bugData.type || BUG_TYPE.BUG,
      assigned_to: bugData.assigned_to || '',
      environment: bugData.environment || '',
      steps_to_reproduce: bugData.steps_to_reproduce || '',
      expected_result: bugData.expected_result || '',
      actual_result: bugData.actual_result || '',
      test_data: bugData.test_data || '',
      attachments: bugData.attachments || ''
    }

    // Load linked test cases
    if (bugData.test_case_ids && bugData.test_case_ids.length > 0) {
      try {
        const testCasePromises = bugData.test_case_ids.map(async (testCaseId) => {
          try {
            const testCase = await testCaseService.getById(testCaseId)
            return testCase
          } catch (error) {
            console.error(`❌ Error loading test case ${testCaseId}:`, error)
            return null
          }
        })
        
        const testCases = await Promise.all(testCasePromises)
        linkedTestCases.value = testCases.filter(tc => tc !== null)
      } catch (tcError) {
        console.error('❌ Error loading linked test cases:', tcError)
        linkedTestCases.value = []
      }
    } else {
      linkedTestCases.value = []
    }
    
    // Load related features and test runs after bug data is loaded
    await Promise.all([
      loadRelatedFeatures(),
      loadLinkedTestRuns(),
      loadActivities()
    ])
  } catch (error) {
    console.error('❌ Error loading bug:', error)
    showError('Failed to load bug')
  } finally {
    loading.value = false
    tabsKey.value++  // Force HLTabs to re-render with updated data
  }
}

const loadUsers = async () => {
  try {
    const usersData = await userService.getAll()
    users.value = usersData || []
  } catch (error) {
    console.error('Error loading users:', error)
    users.value = []
  }
}

const loadRelatedFeatures = async () => {
  try {
    
    const { testRunService } = await import('@/database/services')
    const { featureService } = await import('@/database/services')
    
    const allFeatures = await featureService.getByProject(projectId.value)
    
    const matchedFeatureIds = new Set()
    
    // Method 0: Direct lookup from bug's feature_ids array (PRIMARY METHOD)
    if (bug.value?.feature_ids && bug.value.feature_ids.length > 0) {
      bug.value.feature_ids.forEach(featureId => {
        matchedFeatureIds.add(featureId)
      })
    }
    
    // Method 1: Features that have this bug directly in their bug_ids array (FALLBACK)
    allFeatures.forEach(feature => {
      const bugIds = feature.bug_ids || []
      if (bugIds.includes(bug.value?.id) || bugIds.includes(bugId.value)) {
        matchedFeatureIds.add(feature.id)
      }
    })
    
    // Method 2: Features linked through test runs
    if (bug.value?.test_case_ids && bug.value.test_case_ids.length > 0) {
      const allTestRuns = await testRunService.getByProject(projectId.value)
      
      const relatedTestRunIds = new Set()
      allTestRuns.forEach((testRun) => {
        const testCaseIds = testRun.test_cases?.map(tc => 
          typeof tc === 'string' ? tc : (tc.testCaseId || tc.id)
        ).filter(id => id) || []
        
        const hasMatchingTestCase = bug.value.test_case_ids.some(bugTcId => testCaseIds.includes(bugTcId))
        if (hasMatchingTestCase) {
          relatedTestRunIds.add(testRun.id)
        }
        
        const hasBugInTestCase = testRun.test_cases?.some(tc => {
          const bugIds = tc.bugIds || []
          return bugIds.includes(bug.value?.id) || bugIds.includes(bugId.value)
        })
        
        if (hasBugInTestCase) {
          relatedTestRunIds.add(testRun.id)
        }
      })
      
      
      if (relatedTestRunIds.size > 0) {
        allFeatures.forEach(feature => {
          const testRunIds = feature.test_run_ids || (feature.test_run_id ? [feature.test_run_id] : [])
          const hasMatch = testRunIds.some(trId => relatedTestRunIds.has(trId))
          
          if (hasMatch) {
            matchedFeatureIds.add(feature.id)
          }
        })
      }
    }
    
    relatedFeatures.value = allFeatures.filter(feature => matchedFeatureIds.has(feature.id))
  } catch (error) {
    console.error('❌ Error loading related features:', error)
    relatedFeatures.value = []
  }
}

const loadLinkedTestRuns = async () => {
  try {
    
    if (!bug.value?.test_case_ids || bug.value.test_case_ids.length === 0) {
      linkedTestRuns.value = []
      return
    }
    
    const { testRunService } = await import('@/database/services')
    const allTestRuns = await testRunService.getByProject(projectId.value)
    
    linkedTestRuns.value = allTestRuns.filter((testRun) => {
      const testCaseIds = testRun.test_cases?.map(tc => 
        typeof tc === 'string' ? tc : (tc.testCaseId || tc.id)
      ).filter(id => id) || []
      
      const hasMatchingTestCase = bug.value.test_case_ids.some(bugTcId => testCaseIds.includes(bugTcId))
      if (hasMatchingTestCase) {
        return true
      }
      
      const hasBugInTestCase = testRun.test_cases?.some(tc => {
        const bugIds = tc.bugIds || []
        return bugIds.includes(bug.value.id) || bugIds.includes(bugId.value)
      })
      
      if (hasBugInTestCase) {
        return true
      }
      
      return false
    })
    
  } catch (error) {
    console.error('❌ Error loading linked test runs:', error)
    linkedTestRuns.value = []
  }
}

const loadActivities = async () => {
  try {
    // Ensure users are loaded before resolving activity user names
    if (!users.value || users.value.length === 0) {
      try {
        users.value = await userStore.getUsers() || []
      } catch (error) {
        console.warn('Error loading users for activities:', error)
      }
    }
    
    let response = bug.value?.activities || []
    
    // Filter out invalid activities (empty arrays, null, undefined, or objects without type)
    const validActivities = response.filter(activity => {
      // Skip if it's null or undefined
      if (!activity || typeof activity !== 'object') return false
      
      // Skip if it's an empty array (corrupted data) - check length property
      if (Array.isArray(activity) && activity.length === 0) return false
      
      // Skip if it's an object that looks like an empty array (has length property but no other fields)
      if (activity.length !== undefined && activity.length === 0 && !activity.id && !activity.timestamp && !activity.type) {
        return false
      }
      
      // Keep if it has at least an id, timestamp, or type (basic validation)
      if (activity.id || activity.timestamp || activity.type) return true
      
      return false
    })
    
    // Resolve user names for all activities
    const activitiesWithUserNames = await Promise.all(
      (validActivities || []).map(async (activity) => {
        // If activity already has user_name, use it
        if (activity.user_name) {
          return activity
        }
        
        // Try to resolve user name from user_id
        const userId = activity.user_id || activity.created_by
        if (userId) {
          try {
            // First check in users.value (already loaded)
            let user = users.value.find(u => u.id === userId || u.uid === userId)
            
            // If not found, try to fetch from userStore
            if (!user) {
              try {
                user = await userStore.getUserById(userId)
              } catch (fetchError) {
                console.warn(`Could not fetch user ${userId} from userStore:`, fetchError)
              }
            }
            
            // Set user_name if user found
            if (user) {
              activity.user_name = user.name || user.displayName || user.email || 'Unknown User'
            } else {
              // If user not found, check if it's the current user
              if (authStore.user && (authStore.user.id === userId || authStore.user.uid === userId)) {
                activity.user_name = authStore.user.name || authStore.user.email || 'Unknown User'
              } else {
                activity.user_name = 'Unknown User'
              }
            }
          } catch (error) {
            console.warn(`Error resolving user for activity ${activity.id}:`, error)
            // Fallback to current user if available
            if (authStore.user) {
              activity.user_name = authStore.user.name || authStore.user.email || 'Unknown User'
            } else {
              activity.user_name = 'Unknown User'
            }
          }
        } else {
          // No user_id or created_by, check if it's a system activity or use current user
          if (authStore.user) {
            activity.user_name = authStore.user.name || authStore.user.email || 'Unknown User'
          } else {
            activity.user_name = 'Unknown User'
          }
        }
        
        return activity
      })
    )
    
    // Filter out redundant generic "updated" activities when specific field_updated activities exist
    const filteredActivities = activitiesWithUserNames.filter((activity, index, arr) => {
      // Keep all non-updated activities (created, deleted, restored, etc.)
      if (activity.type !== 'updated') {
        return true
      }
      
      // For generic "updated" activities, check if there are specific field_updated activities
      // at the same time (within 1 second) from the same user
      const activityTime = activity.timestamp ? new Date(activity.timestamp).getTime() : 0
      const activityUserId = activity.user_id || activity.created_by
      
      // Check if there are any field_updated activities at the same time
      const hasSpecificFieldUpdates = arr.some(otherActivity => {
        if (otherActivity === activity) return false
        if (otherActivity.type !== 'field_updated') return false
        
        const otherTime = otherActivity.timestamp ? new Date(otherActivity.timestamp).getTime() : 0
        const otherUserId = otherActivity.user_id || otherActivity.created_by
        const timeDiff = Math.abs(activityTime - otherTime)
        
        // Same user and within 1 second
        return otherUserId === activityUserId && timeDiff < 1000
      })
      
      // Remove generic "updated" activity if specific field updates exist
      return !hasSpecificFieldUpdates
    })
    
    // Sort activities in descending order (newest first)
    activities.value = filteredActivities.sort((a, b) => {
      const dateA = a.timestamp ? new Date(a.timestamp) : new Date(0)
      const dateB = b.timestamp ? new Date(b.timestamp) : new Date(0)
      return dateB - dateA
    })
  } catch (error) {
    console.error('❌ Error loading activities:', error)
    activities.value = []
  }
}

const viewTestCase = (testCaseId) => {
  if (testCaseId) {
    const url = `/projects/${projectId.value}/test-cases/${testCaseId}`
    window.open(url, '_blank')
  }
}

const viewTestRun = (testRunId) => {
  if (testRunId) {
    const url = `/projects/${projectId.value}/test-runs/${testRunId}`
    window.open(url, '_blank')
  }
}

const formatEnvironment = (env) => {
  if (!env) return 'N/A'
  return env.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')
}

const getTestRunStatusVariant = (status) => {
  if (!status) return 'secondary'
  const s = status.toLowerCase()
  if (s === 'completed') return 'success'
  if (s === 'in_progress') return 'info'
  if (s === 'failed') return 'danger'
  if (s === 'blocked') return 'warning'
  return 'secondary'
}

const formatTestRunStatus = (status) => {
  if (!status) return 'Unknown'
  const labels = {
    'pending': 'Pending',
    'in_progress': 'In Progress',
    'completed': 'Completed',
    'failed': 'Failed',
    'blocked': 'Blocked',
    'passed': 'Passed'
  }
  return labels[status.toLowerCase()] || status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, ' ')
}

const viewFeature = (featureId) => {
  if (featureId) {
    const url = `/projects/${projectId.value}/features/${featureId}`
    window.open(url, '_blank')
  }
}

const goBack = () => {
  // Check if user came from a test run page
  const testRunId = route.query.testRunId || route.query.runId
  if (testRunId) {
    router.push(`/test-runs/${testRunId}`)
    return
  }
  
  // Check if user came from assigned-to-me page
  const fromRoute = route.query.from
  if (fromRoute === 'assigned-to-me') {
    router.push('/assigned-to-me')
    return
  }
  
  // Check for projectId to navigate to project bugs page
  const projectId = route.params.projectId || bug.value?.project_id
  if (projectId) {
    router.push(`/projects/${projectId}/bugs`)
    return
  }
  
  // Fallback to browser back
  router.back()
}

const getTypeLabel = (type) => {
  const option = BUG_TYPE_OPTIONS.find(o => o.value === type)
  return option ? option.label : type
}

const getPriorityLabel = (priority) => {
  const option = BUG_PRIORITY_OPTIONS.find(o => o.value === priority)
  return option ? option.label : priority
}

const getSeverityLabel = (severity) => {
  const option = BUG_SEVERITY_OPTIONS.find(o => o.value === severity)
  return option ? option.label : severity
}

const getEnvironmentLabel = (environment) => {
  const option = ENVIRONMENT_OPTIONS.find(o => o.value === environment)
  return option ? option.label : environment
}

const getStatusLabel = (status) => {
  const option = BUG_STATUS_OPTIONS.find(o => o.value === status)
  return option ? option.label : status
}

const getStatusColor = (status) => {
  const colorMap = {
    'open': 'error',            // Red - needs attention
    'in_progress': 'info',      // Blue - being worked on
    'resolved': 'success',      // Green - fixed
    'closed': 'default',        // Gray - completed/archived
    'reopened': 'warning',      // Orange - needs re-attention
    'blocked': 'error',         // Red - blocked (same as open, but different context)
    'not_a_bug': 'warning',     // Orange/Yellow - not a bug
    'setup_issue': 'info'       // Blue - setup issue (same as in_progress)
  }
  return colorMap[status] || 'default'
}

const getPriorityColor = (priority) => {
  const colorMap = {
    'critical': 'danger',
    'high': 'warning',
    'medium': 'info',
    'low': 'secondary'
  }
  return colorMap[priority] || 'secondary'
}

const getSeverityColor = (severity) => {
  const colorMap = {
    'critical': 'danger', // Red
    'major': 'warning', // Orange
    'moderate': 'info', // Blue (distinct from major)
    'minor': 'success', // Green (distinct from moderate)
  }
  return colorMap[severity] || 'secondary'
}

const getTypeColor = (type) => {
  const colorMap = {
    'bug': 'danger',
    'enhancement': 'info',
    'task': 'secondary'
  }
  return colorMap[type] || 'secondary'
}

const getEnvironmentColor = (env) => {
  if (!env) return 'secondary'
  if (env.includes('production') || env.includes('prod')) return 'danger'
  if (env.includes('staging') || env.includes('stage')) return 'warning'
  if (env.includes('qa') || env.includes('testing') || env.includes('test')) return 'info'
  if (env.includes('development') || env.includes('dev')) return 'secondary'
  return 'secondary'
}

const formatDate = (date) => {
  if (!date) return 'Unknown'
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const formatDateOnly = (date) => {
  if (!date) return 'N/A'
  try {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric'
    })
  } catch (error) {
    return 'Invalid Date'
  }
}

// Feature Helper Methods
const getFeatureStatusLabel = (status) => {
  const labels = {
    'open': 'Open',
    'in_progress': 'In Progress',
    'in-progress': 'In Progress',
    'completed': 'Completed',
    'closed': 'Closed',
    'blocked': 'Blocked',
    'in_review': 'In Review',
    'in-review': 'In Review'
  }
  return labels[status] || status?.charAt(0).toUpperCase() + status?.slice(1) || 'Unknown'
}

const getFeaturePriorityLabel = (priority) => {
  const labels = {
    'critical': 'Critical',
    'high': 'High',
    'medium': 'Medium',
    'low': 'Low'
  }
  return labels[priority] || priority?.charAt(0).toUpperCase() + priority?.slice(1) || 'Unknown'
}

const getFeatureStatusVariant = (status) => {
  const variants = {
    'open': 'info',
    'in_progress': 'warning',
    'in-progress': 'warning',
    'completed': 'success',
    'closed': 'secondary',
    'blocked': 'danger',
    'in_review': 'blue',
    'in-review': 'blue'
  }
  return variants[status] || 'secondary'
}

const getFeaturePriorityVariant = (priority) => {
  const variants = {
    'critical': 'danger',
    'high': 'warning',
    'medium': 'info',
    'low': 'secondary'
  }
  return variants[priority] || 'secondary'
}

// Test Case Helper Methods
const getTestCaseStatusLabel = (status) => {
  const labels = {
    'draft': 'Draft',
    'ready': 'Ready',
    'in_review': 'In Review',
    'approved': 'Approved',
    'deprecated': 'Deprecated'
  }
  return labels[status] || status
}

const getTestCaseStatusVariant = (status) => {
  const variants = {
    'draft': 'secondary',
    'ready': 'info',
    'in_review': 'warning',
    'approved': 'success',
    'deprecated': 'danger'
  }
  return variants[status] || 'secondary'
}

const getTestCasePriorityLabel = (priority) => {
  const labels = {
    'critical': 'Critical',
    'high': 'High',
    'medium': 'Medium',
    'low': 'Low'
  }
  return labels[priority] || priority
}

const getTestCasePriorityVariant = (priority) => {
  const variants = {
    'critical': 'danger',
    'high': 'warning',
    'medium': 'info',
    'low': 'secondary'
  }
  return variants[priority] || 'secondary'
}

const getTestCaseAssigneeName = (assigneeId) => {
  if (!assigneeId) return 'Unassigned'
  const user = users.value.find(u => u.id === assigneeId)
  return user ? (user.name || user.email) : 'Unknown'
}

// Comment Methods
const loadComments = async () => {
  // Validate bugId before making API call
  if (!bugId.value) {
    console.warn('⚠️ Cannot load comments: bugId is undefined')
    comments.value = []
    return
  }
  
  try {
    loadingComments.value = true
    
    const loadedComments = await bugService.getComments(bugId.value)
    comments.value = loadedComments
    
  } catch (error) {
    console.error('❌ Error loading comments:', error)
    showError('Failed to load comments')
  } finally {
    loadingComments.value = false
  }
}

const addComment = async () => {
  if (!newComment.value.trim()) return
  
  try {
    addingComment.value = true
    
    const userId = authStore.user?.id || 'unknown'
    const userName = authStore.user?.name || authStore.user?.email || 'Unknown User'
    
    await bugService.addComment(bugId.value, userId, userName, newComment.value.trim())
    
    await loadComments()
    newComment.value = ''
    
  } catch (error) {
    console.error('❌ Error adding comment:', error)
    showError('Failed to add comment')
  } finally {
    addingComment.value = false
  }
}

const startEditComment = (comment) => {
  editingCommentId.value = comment.id
  editingCommentText.value = comment.comment
}

const cancelEditComment = () => {
  editingCommentId.value = null
  editingCommentText.value = ''
}

const saveEditComment = async (comment) => {
  if (!editingCommentText.value.trim()) return
  
  try {
    
    await bugService.updateComment(bugId.value, comment.id, editingCommentText.value.trim())
    
    await loadComments()
    cancelEditComment()
    
    showSuccess('Comment updated successfully')
  } catch (error) {
    console.error('❌ Error updating comment:', error)
    showError('Failed to update comment')
  }
}

const deleteComment = async (comment) => {
  if (!confirm('Are you sure you want to delete this comment?')) return
  
  try {
    
    const userId = authStore.user?.id || 'unknown'
    await bugService.deleteComment(bugId.value, comment.id, userId)
    
    await loadComments()
    
    showSuccess('Comment deleted successfully')
  } catch (error) {
    console.error('❌ Error deleting comment:', error)
    showError('Failed to delete comment')
  }
}

const canEditComment = (comment) => {
  const currentUserId = authStore.user?.id
  return comment.user_id === currentUserId
}

const canDeleteComment = (comment) => {
  const currentUserId = authStore.user?.id
  return comment.user_id === currentUserId
}

const getInitials = (name) => {
  if (!name) return '?'
  const names = name.split(' ')
  if (names.length >= 2) {
    return (names[0][0] + names[names.length - 1][0]).toUpperCase()
  }
  return name.substring(0, 2).toUpperCase()
}

const formatCommentDate = (date) => {
  if (!date) return 'Unknown'
  
  const commentDate = new Date(date)
  const now = new Date()
  const diffMs = now - commentDate
  const diffMinutes = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)
  
  if (diffMinutes < 1) return 'Just now'
  if (diffMinutes < 60) return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`
  
  return commentDate.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const getActivityIconClass = (type) => {
  const classes = {
    'created': 'bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400',
    'updated': 'bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400',
    'linked': 'bg-purple-100 dark:bg-purple-900 text-purple-600 dark:text-purple-400',
    'deleted': 'bg-red-100 dark:bg-red-900 text-red-600 dark:text-red-400',
    'restored': 'bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400',
    'default': 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
  }
  return classes[type] || classes.default
}

// Inline editing functions
const startEdit = (field, currentValue) => {
  if (!can.editBugs()) return
  editingField.value = field
  
  // Convert array to string for editing steps_to_reproduce
  if (field === 'steps_to_reproduce' && Array.isArray(currentValue)) {
    editForm.value[field] = currentValue.join('\n')
  } else {
    editForm.value[field] = currentValue || ''
  }
  
  nextTick(() => {
    if (field === 'title') {
      if (titleInput.value) {
        titleInput.value.focus()
      }
    } else if (field === 'steps_to_reproduce') {
      if (stepsInput.value) {
        stepsInput.value.focus()
      }
    } else if (field === 'expected_result') {
      if (expectedResultInput.value) {
        expectedResultInput.value.focus()
      }
    } else if (field === 'actual_result') {
      if (actualResultInput.value) {
        actualResultInput.value.focus()
      }
    } else if (field === 'test_data') {
      if (testDataInput.value) {
        testDataInput.value.focus()
      }
    } else if (field === 'attachments') {
      if (attachmentsInput.value) {
        attachmentsInput.value.focus()
      }
    }
  })
}

const cancelEdit = () => {
  editingField.value = null
}

// Helper function to format field name for toast messages
const getFieldDisplayName = (field) => {
  const fieldNameMap = {
    'assigned_to': 'Assigned To',
    'status': 'Status',
    'priority': 'Priority',
    'severity': 'Severity',
    'type': 'Type',
    'environment': 'Environment',
    'title': 'Title',
    'steps_to_reproduce': 'Steps to Reproduce',
    'expected_result': 'Expected Result',
    'actual_result': 'Actual Result',
    'test_data': 'Test Data',
    'attachments': 'Attachments'
  }
  return fieldNameMap[field] || field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
}

const handleFieldChange = async (field, value) => {
  if (!can.editBugs()) return
  
  try {
    const userId = authStore.user?.id || authStore.user?.uid
    const userName = authStore.user?.name || authStore.user?.email || 'Unknown'
    
    const updates = { 
      [field]: value,
      updated_by: userId,
      updated_by_name: userName
    }
    
    // Use updateBug which handles activity tracking
    await bugService.updateBug(bug.value.id, updates, userId)
    await loadBug()
    await loadActivities() // Reload activities to show the new activity
    
    // Format success message with user-friendly field name
    let successMessage = `${getFieldDisplayName(field)} updated successfully`
    
    // For assigned_to, include the user's name if available
    if (field === 'assigned_to' && value) {
      const assignedUser = users.value.find(u => u.id === value || u.uid === value)
      if (assignedUser) {
        const userName = assignedUser.name || assignedUser.email || 'Unknown'
        successMessage = `Assigned To updated to ${userName} successfully`
      } else if (value === '' || value === null) {
        successMessage = 'Assigned To updated to Unassigned successfully'
      }
    }
    
    showSuccess(successMessage)
  } catch (error) {
    console.error(`Update ${field} error:`, error)
    showError(`Failed to update ${getFieldDisplayName(field)}`)
  }
}

const saveEdit = async (field) => {
  try {
    const userId = authStore.user?.id
    const userName = authStore.user?.name || authStore.user?.email || 'Unknown'
    
    // Store the old value for display validation
    const oldValue = bug.value?.[field]
    let newValue = editForm.value[field]
    
    // Convert steps_to_reproduce from string to array if needed
    if (field === 'steps_to_reproduce' && typeof newValue === 'string') {
      newValue = newValue.split('\n').filter(step => step.trim().length > 0)
    }
    
    // Don't update if value hasn't changed
    if (JSON.stringify(oldValue) === JSON.stringify(newValue)) {
      cancelEdit()
      return
    }
    
    const updates = { 
      [field]: newValue,
      updated_by: userId,
      updated_by_name: userName
    }
    
    // updateBug already handles activity tracking internally
    await bugService.updateBug(bugId.value, updates, userId)
    await loadBug()
    cancelEdit()
  } catch (error) {
    console.error('Update error:', error)
    showError('Failed to update bug')
    cancelEdit()
  }
}

// Image compression helper
const compressImageFile = async (file) => {
  const maxSizeMB = 5
  const maxSizeBytes = maxSizeMB * 1024 * 1024
  
  // If file is already under 5MB, return as-is
  if (file.size <= maxSizeBytes) {
    return file
  }

  // Check if it's an image file
  if (!file.type.startsWith('image/')) {
    // For non-image files, just check size
    if (file.size > maxSizeBytes) {
      throw new Error(
        `File "${file.name}" exceeds 5MB limit (${(file.size / 1024 / 1024).toFixed(2)}MB). ` +
        `Please use a smaller file.`
      )
    }
    return file
  }

  try {
    // Progressive compression strategy
    const compressionAttempts = [
      { maxWidthOrHeight: 1920, initialQuality: 0.9 },
      { maxWidthOrHeight: 1280, initialQuality: 0.7 },
      { maxWidthOrHeight: 960, initialQuality: 0.5 },
      { maxWidthOrHeight: 720, initialQuality: 0.3 },
    ]

    let lastCompressed = null
    let lastSize = file.size

    for (const attempt of compressionAttempts) {
      const options = {
        maxSizeMB: maxSizeMB,
        maxWidthOrHeight: attempt.maxWidthOrHeight,
        useWebWorker: true,
        fileType: file.type,
        initialQuality: attempt.initialQuality,
      }

      const compressedFile = await imageCompression(file, options)
      
      // If we got it under 5MB, return it
      if (compressedFile.size <= maxSizeBytes) {
        return compressedFile
      }
      
      // Track the best compression so far
      if (compressedFile.size < lastSize) {
        lastCompressed = compressedFile
        lastSize = compressedFile.size
      }
    }

    // If we still have a compressed version that's better than original, use it
    // But still check if it's under 5MB
    if (lastCompressed && lastCompressed.size <= maxSizeBytes) {
      return lastCompressed
    }

    // Final check - if still too large after all attempts, throw error
    const finalSize = lastCompressed ? lastCompressed.size : file.size
    throw new Error(
      `File "${file.name}" exceeds 5MB limit even after compression. ` +
      `Original size: ${(file.size / 1024 / 1024).toFixed(2)}MB, ` +
      `Compressed size: ${(finalSize / 1024 / 1024).toFixed(2)}MB. ` +
      `Please use a smaller image.`
    )
  } catch (error) {
    // If compression fails, check if it's our custom error
    if (error.message && error.message.includes('exceeds 5MB limit')) {
      throw error
    }
    // Otherwise, log and check original file size
    console.error('Error compressing image:', error)
    // If compression fails, validate original file size
    if (file.size > maxSizeBytes) {
      throw new Error(
        `File "${file.name}" exceeds 5MB limit (${(file.size / 1024 / 1024).toFixed(2)}MB). ` +
        `Compression failed. Please use a smaller image.`
      )
    }
    return file
  }
}

// Attachment upload handlers
const handleAttachmentChange = async (options) => {
  const { fileList } = options
  
  // Count uploaded files in the current fileList (those that have URLs - already uploaded)
  const uploadedInListCount = fileList.filter(f => f.url).length
  
  // Count pending files in the current fileList (those without URLs but have file objects)
  const pendingInListCount = fileList.filter(f => !f.url && f.file).length
  
  // Total count is the sum of uploaded and pending files in the current list
  const totalCount = uploadedInListCount + pendingInListCount
  
  if (totalCount > 5) {
    const availableSlots = 5 - uploadedInListCount
    if (availableSlots <= 0) {
      showError('Maximum 5 attachments allowed. Please remove some files before adding new ones.')
    } else {
      showError(`Maximum 5 attachments allowed. You can only add ${availableSlots} more file(s).`)
    }
    // Reset file list to previous state - keep only uploaded files
    attachmentFileList.value = attachmentFileList.value.filter(f => f.url)
    return
  }
  
  // Filter out files that are already uploaded (have url) or already being processed
  // Check against both attachmentFileList and pendingFiles to avoid processing duplicates
  const filesToUpload = fileList.filter(file => {
    if (file.url || !file.file) return false // Skip uploaded files or files without file objects
    
    // Check if this file is already in attachmentFileList (being processed or processed)
    const alreadyInList = attachmentFileList.value.some(existing => {
      if (!existing.file || !file.file) return false
      return existing.file.name === file.file.name &&
             existing.file.size === file.file.size &&
             existing.file.lastModified === file.file.lastModified
    })
    if (alreadyInList) return false
    
    // Check if this file is already in pendingFiles (queued for upload)
    const alreadyPending = pendingFiles.value.some(pending => {
      if (!pending.file || !file.file) return false
      return pending.file.name === file.file.name &&
             pending.file.size === file.file.size &&
             pending.file.lastModified === file.file.lastModified
    })
    if (alreadyPending) return false
    
    return true
  })
  
  if (filesToUpload.length === 0) {
    return
  }

  // Clear any existing debounce timer
  if (uploadDebounceTimer.value) {
    clearTimeout(uploadDebounceTimer.value)
    uploadDebounceTimer.value = null
  }

  // Mark compression as in progress
  isCompressing.value = true

  try {
    // Compress all files in parallel and wait for all to complete
    const compressionPromises = filesToUpload.map(async (fileInfo) => {
      const file = fileInfo.file
      if (!file) return null

      try {
        // Update file status to show compression in progress
        const fileInList = attachmentFileList.value.find(f => f === fileInfo)
        if (fileInList) {
          fileInList.status = 'compressing'
        }

        // Compress image if needed
        const compressedFile = await compressImageFile(file)
        
        // Create new file info with compressed file
        const processedFileInfo = {
          ...fileInfo,
          file: compressedFile,
          // Update file name to reflect compression if needed
          name: compressedFile.name || file.name,
        }
        
        return processedFileInfo
      } catch (error) {
        // Show error and remove this file from the list
        showError(error.message || `Failed to process file "${file.name}"`)
        // Remove the file from the file list
        attachmentFileList.value = attachmentFileList.value.filter(f => f !== fileInfo)
        return null
      }
    })

    // Wait for ALL compressions to complete before proceeding
    const processedFiles = (await Promise.all(compressionPromises)).filter(f => f !== null)

    if (processedFiles.length === 0) {
      return
    }

    // Add processed files to pending queue (avoid duplicates)
    processedFiles.forEach(fileInfo => {
      // Check if file is not already in pending queue
      const alreadyPending = pendingFiles.value.some(pf => 
        pf.file && fileInfo.file && 
        pf.file.name === fileInfo.file.name && 
        pf.file.size === fileInfo.file.size &&
        pf.file.lastModified === fileInfo.file.lastModified
      )
      if (!alreadyPending) {
        pendingFiles.value.push(fileInfo)
      }
    })
    
    // Update the file list with processed files
    // Merge properly: keep all existing files and add new processed files, avoiding duplicates
    // First, filter out processed files that are already in attachmentFileList to avoid duplicates
    const newProcessedFiles = processedFiles.filter(pf => {
      // Check if this processed file is already in attachmentFileList
      const alreadyInList = attachmentFileList.value.some(existing => {
        if (!existing.file || !pf.file) return false
        return existing.file.name === pf.file.name &&
               existing.file.size === pf.file.size &&
               existing.file.lastModified === pf.file.lastModified
      })
      return !alreadyInList
    })
    
    // Keep all existing files (both uploaded and those being processed by other concurrent calls)
    const existingFiles = attachmentFileList.value.filter(existing => {
      // Keep files that are already uploaded (have URLs)
      if (existing.url) return true
      
      // Keep files that are not in the newly processed files (to avoid removing files being processed by other concurrent calls)
      const isInNewProcessedFiles = newProcessedFiles.some(pf => 
        existing.file && pf.file &&
        existing.file.name === pf.file.name &&
        existing.file.size === pf.file.size &&
        existing.file.lastModified === pf.file.lastModified
      )
      return !isInNewProcessedFiles
    })
    
    attachmentFileList.value = [
      ...existingFiles,
      ...newProcessedFiles // Add only truly new processed files
    ]

    // Final validation - check file count (including already uploaded and pending)
    const existingUploadedCount = attachmentLinks.value.length
    const totalPending = pendingFiles.value.length
    const totalCount = existingUploadedCount + totalPending
    
    if (totalCount > 5) {
      showError('Maximum 5 attachments allowed. Please remove some files.')
      // Reset file list to previous state - keep only uploaded files
      attachmentFileList.value = attachmentFileList.value.filter(f => f.url)
      pendingFiles.value = []
      return
    }

    // All compressions are complete, now schedule upload of all files
    // Clear any existing debounce timer and set a new one
    // This ensures we wait for ALL compression batches to complete before uploading
    if (uploadDebounceTimer.value) {
      clearTimeout(uploadDebounceTimer.value)
      uploadDebounceTimer.value = null
    }

    // Schedule upload with debounce - this will be called by each batch,
    // but only the last one will actually execute after the debounce period
    uploadDebounceTimer.value = setTimeout(async () => {
      // Double-check that compression is complete and no upload is in progress
      if (isCompressing.value || uploadingAttachments.value) {
        // If compression is still happening or upload is in progress, wait a bit more
        uploadDebounceTimer.value = setTimeout(async () => {
          await performUpload()
        }, 300)
        return
      }
      await performUpload()
    }, 500) // Increased debounce to 500ms to give more time for all batches to complete
  } finally {
    // Mark compression as complete
    isCompressing.value = false
  }
}

const performUpload = async () => {
  // Clear the timer
  if (uploadDebounceTimer.value) {
    clearTimeout(uploadDebounceTimer.value)
    uploadDebounceTimer.value = null
  }

  // Prevent multiple simultaneous uploads
  if (uploadingAttachments.value) {
    return
  }

  // Wait for any ongoing compression to complete before uploading
  if (isCompressing.value) {
    // Retry after a short delay if compression is still in progress
    uploadDebounceTimer.value = setTimeout(async () => {
      await performUpload()
    }, 200)
    return
  }

  // Get all pending files - but DON'T clear pendingFiles yet
  // We'll clear it only after successful upload
  // This allows multiple calls to see all files if the first one is blocked
  const filesToUpload = [...pendingFiles.value]

  if (filesToUpload.length === 0) {
    return
  }

  // Now clear the files we're about to upload from pendingFiles
  // Remove only the files we're uploading, not all files
  // This allows other compression batches to add their files while we're uploading
  pendingFiles.value = pendingFiles.value.filter(pending => {
    return !filesToUpload.some(upload => 
      pending.file && upload.file &&
      pending.file.name === upload.file.name &&
      pending.file.size === upload.file.size &&
      pending.file.lastModified === upload.file.lastModified
    )
  })

  // Final validation - check total count (uploaded + files being uploaded)
  const existingUploadedCount = attachmentLinks.value.length
  const totalCount = existingUploadedCount + filesToUpload.length
  
  if (totalCount > 5) {
    showError('Maximum 5 attachments allowed. Please remove some files.')
    attachmentFileList.value = attachmentFileList.value.filter(f => f.url)
    return
  }

  try {
    uploadingAttachments.value = true

    const { getAccessToken } = await import('@/utils/httpClient')
    const API_BASE_URL = import.meta.env.VITE_API_BASE_URL
    const endpoint = `/bugs/${bugId.value}/attachments`
    const url = !API_BASE_URL || API_BASE_URL === ''
      ? `/hltestmanager${endpoint.startsWith('/') ? endpoint : '/' + endpoint}`
      : `${API_BASE_URL}${endpoint.startsWith('/') ? endpoint : '/' + endpoint}`

    let successCount = 0
    let fiveAlreadyShown = false

    for (const fileInfo of filesToUpload) {
      if (!fileInfo.file) continue

      const formData = new FormData()
      formData.append('files', fileInfo.file)

      try {
        const response = await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest()

          xhr.upload.addEventListener('progress', (e) => {
            if (e.lengthComputable) {
              const percentComplete = Math.round((e.loaded / e.total) * 100)
              const fileInList = attachmentFileList.value.find(f =>
                f.file && fileInfo.file &&
                f.file.name === fileInfo.file.name &&
                f.file.size === fileInfo.file.size &&
                f.file.lastModified === fileInfo.file.lastModified
              )
              if (fileInList) {
                fileInList.percentage = percentComplete
                fileInList.status = 'uploading'
              }
            }
          })

          xhr.addEventListener('load', () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              try {
                resolve(JSON.parse(xhr.responseText))
              } catch (e) {
                resolve(xhr.responseText)
              }
            } else {
              try {
                const errBody = JSON.parse(xhr.responseText)
                const msg = errBody.message || errBody.error?.message || `Request failed with status ${xhr.status}`
                reject(new Error(msg))
              } catch (e) {
                reject(new Error(`Request failed with status ${xhr.status}`))
              }
            }
          })

          xhr.addEventListener('error', () => reject(new Error('Network error occurred')))
          xhr.addEventListener('abort', () => reject(new Error('Upload was aborted')))

          xhr.open('POST', url)
          const accessToken = getAccessToken()
          if (accessToken) {
            xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`)
          }
          xhr.send(formData)
        })

        if (response && response.urls && response.urls.length > 0) {
          successCount += response.urls.length
          const fileInList = attachmentFileList.value.find(f =>
            f.file && fileInfo.file &&
            f.file.name === fileInfo.file.name &&
            f.file.size === fileInfo.file.size &&
            f.file.lastModified === fileInfo.file.lastModified
          )
          if (fileInList) {
            fileInList.percentage = 100
            fileInList.status = 'finished'
          }
        }
      } catch (err) {
        const msg = err?.message || ''
        const isFiveAlready = /5 images are already there|delete one of them/i.test(msg)
        if (isFiveAlready && !fiveAlreadyShown) {
          showError('5 images are already there. Please delete one of them to add more.')
          fiveAlreadyShown = true
        } else if (!isFiveAlready) {
          showError(msg || 'Failed to upload attachment')
        }
        const fileInList = attachmentFileList.value.find(f =>
          f.file && fileInfo.file &&
          f.file.name === fileInfo.file.name &&
          f.file.size === fileInfo.file.size &&
          f.file.lastModified === fileInfo.file.lastModified
        )
        if (fileInList) {
          fileInList.status = 'error'
        }
        // Stop uploading remaining files when limit reached
        if (isFiveAlready) break
      }
    }

    if (successCount > 0) {
      showSuccess(`Successfully uploaded ${successCount} file(s)`)
      attachmentFileList.value = []
      await loadBug()
    }

    if (pendingFiles.value.length > 0 && !isCompressing.value) {
      uploadDebounceTimer.value = setTimeout(async () => {
        await performUpload()
      }, 300)
    }

    if (successCount === 0) {
      setTimeout(() => {
        attachmentFileList.value = attachmentFileList.value.filter(f => f.url || f.status !== 'error')
      }, 2000)
    }
  } catch (error) {
    console.error('Error uploading attachments:', error)
    filesToUpload.forEach((fileInfo) => {
      const fileInList = attachmentFileList.value.find(f =>
        f.file && fileInfo.file &&
        f.file.name === fileInfo.file.name &&
        f.file.size === fileInfo.file.size &&
        f.file.lastModified === fileInfo.file.lastModified
      )
      if (fileInList) {
        fileInList.status = 'error'
      }
    })
    showError(error?.message || 'Failed to upload attachments')
    setTimeout(() => {
      attachmentFileList.value = attachmentFileList.value.filter(f => f.url || f.status !== 'error')
    }, 2000)
  } finally {
    uploadingAttachments.value = false
  }
}

const handleAttachmentRemove = (options) => {
  const { file } = options
  attachmentFileList.value = attachmentFileList.value.filter(f => f.id !== file.id)
  
  // Also remove from pending files if it's there
  if (file.file) {
    pendingFiles.value = pendingFiles.value.filter(pf => 
      !pf.file || 
      pf.file.name !== file.file.name || 
      pf.file.size !== file.file.size ||
      pf.file.lastModified !== file.file.lastModified
    )
  }
  
  // If we removed a file and there are no more pending files, clear any pending timer
  if (pendingFiles.value.length === 0 && uploadDebounceTimer.value) {
    clearTimeout(uploadDebounceTimer.value)
    uploadDebounceTimer.value = null
  }
}

const openModalForAttachment = (url) => {
  const index = attachmentLinks.value.indexOf(url)
  if (index !== -1) {
    currentImageIndex.value = index
    currentImageUrl.value = url
    imagePreviewModalOpen.value = true
  }
}

const closeImagePreview = () => {
  imagePreviewModalOpen.value = false
  currentImageUrl.value = ''
  currentImageIndex.value = -1
}

const onCarouselIndexUpdate = (newIndex) => {
  currentImageIndex.value = newIndex
  if (attachmentLinks.value[newIndex]) {
    currentImageUrl.value = attachmentLinks.value[newIndex]
  }
}

const removeAttachment = async (urlToRemove) => {
  if (!confirm('Are you sure you want to remove this attachment?')) return
  
  try {
    // Get current attachments
    const currentAttachments = attachmentLinks.value
    const updatedAttachments = currentAttachments.filter(url => url !== urlToRemove)
    
    // Convert array to string format (newline-separated) as expected by the schema
    const attachmentsString = updatedAttachments.join('\n')
    
    // Update bug with removed attachment
    await bugService.updateBug(bugId.value, { attachments: attachmentsString }, authStore.user?.id)
    showSuccess('Attachment removed successfully')
    await loadBug()
  } catch (error) {
    console.error('Error removing attachment:', error)
    showError('Failed to remove attachment')
  }
}

const handleImageError = (event) => {
  // Replace broken image with a placeholder
  event.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect width="200" height="200" fill="%23f3f4f6"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%239ca3af" font-family="sans-serif" font-size="14"%3EImage not found%3C/text%3E%3C/svg%3E'
}

const deleteBug = async () => {
  if (!confirm('Are you sure you want to delete this bug?')) return
  
  try {
    const userId = authStore.user?.uid || authStore.user?.id
    await bugService.softDelete(bugId.value, userId)
    showSuccess('Bug deleted successfully')
    router.push(`/projects/${projectId.value}/bugs`)
  } catch (error) {
    console.error('Delete bug error:', error)
    showError('Failed to delete bug')
  }
}

const restoreBug = async () => {
  if (!confirm('Are you sure you want to restore this bug?')) return
  
  try {
    const userId = authStore.user?.uid || authStore.user?.id
    await bugService.restore(bugId.value, userId)
    showSuccess('Bug restored successfully')
    await loadBug()
  } catch (error) {
    console.error('Restore bug error:', error)
    showError('Failed to restore bug')
  }
}

const cloneBug = async () => {
  if (!confirm('Are you sure you want to clone this bug?')) return
  
  try {
    const userId = authStore.user?.uid || authStore.user?.id
    const userName = authStore.user?.name || authStore.user?.email || 'Unknown'
    
    // Convert attachments to string format if it's an array
    let attachmentsString = ''
    if (bug.value.attachments) {
      if (Array.isArray(bug.value.attachments)) {
        // Convert array to newline-separated string
        attachmentsString = bug.value.attachments
          .filter(att => att && (typeof att === 'string' ? att.trim() : att.url || att.name))
          .map(att => typeof att === 'string' ? att : (att.url || att.name || ''))
          .filter(att => att.trim())
          .join('\n')
      } else if (typeof bug.value.attachments === 'string') {
        attachmentsString = bug.value.attachments
      }
    }
    
    // Convert steps_to_reproduce to array if it's a string
    let stepsArray = []
    if (bug.value.steps_to_reproduce) {
      if (Array.isArray(bug.value.steps_to_reproduce)) {
        stepsArray = bug.value.steps_to_reproduce
      } else if (typeof bug.value.steps_to_reproduce === 'string') {
        stepsArray = bug.value.steps_to_reproduce.split('\n').filter(step => step.trim())
      }
    }
    
    // Generate the next bugId for the cloned bug
    const newBugId = await bugService.getNextBugId()
    
    // Create a cloned bug object
    const clonedBugData = {
      bugId: newBugId, // Set the friendly bug ID
      title: `${bug.value.title} (Copy)`,
      description: bug.value.description,
      status: 'open', // Reset status for clone
      priority: bug.value.priority,
      severity: bug.value.severity,
      type: bug.value.type,
      environment: bug.value.environment,
      steps_to_reproduce: stepsArray,
      expected_result: bug.value.expected_result,
      actual_result: bug.value.actual_result,
      test_data: bug.value.test_data || '',
      attachments: attachmentsString, // Use string format
      assigned_to: bug.value.assigned_to,
      project_id: bug.value.project_id,
      created_by: userId,
      created_by_name: userName,
      // Clone linked relationships
      test_case_ids: bug.value.test_case_ids || [],
      feature_ids: bug.value.feature_ids || []
      // Note: createBug will automatically add a 'created' activity
    }
    
    // Use createBug which handles proper data transformation
    const clonedBug = await bugService.createBug(clonedBugData)
    
    if (!clonedBug || !clonedBug.id) {
      throw new Error('Failed to create cloned bug - no ID returned')
    }
    
    // Update linked test cases to include the cloned bug
    if (clonedBugData.test_case_ids && clonedBugData.test_case_ids.length > 0) {
      for (const testCaseId of clonedBugData.test_case_ids) {
        try {
          const testCase = await testCaseService.getById(testCaseId)
          if (testCase) {
            const bugIds = testCase.bug_ids || []
            if (!bugIds.includes(clonedBug.id)) {
              bugIds.push(clonedBug.id)
              await testCaseService.update(testCaseId, { bug_ids: bugIds })
            }
          }
        } catch (error) {
          console.error(`❌ Error linking test case ${testCaseId} to cloned bug:`, error)
        }
      }
    }
    
    // Update linked features to include the cloned bug
    if (clonedBugData.feature_ids && clonedBugData.feature_ids.length > 0) {
      const { featureService } = await import('@/database/services')
      for (const featureId of clonedBugData.feature_ids) {
        try {
          const feature = await featureService.getById(featureId)
          if (feature) {
            const bugIds = feature.bug_ids || []
            if (!bugIds.includes(clonedBug.id)) {
              bugIds.push(clonedBug.id)
              await featureService.update(featureId, { bug_ids: bugIds })
            }
          }
        } catch (error) {
          console.error(`❌ Error linking feature ${featureId} to cloned bug:`, error)
        }
      }
    }
    
    showSuccess('Bug cloned successfully')
    
    // Navigate to the cloned bug's detail page
    await router.push(`/projects/${projectId.value}/bugs/${clonedBug.id}`)
  } catch (error) {
    console.error('❌ Clone bug error:', error)
    showError(`Failed to clone bug: ${error.message}`)
  }
}

// Computed for filtered dropdowns
const filteredAvailableFeatures = computed(() => {
  if (!featureSearchQuery.value.trim()) {
    return availableFeatures.value
  }
  const query = featureSearchQuery.value.toLowerCase()
  return availableFeatures.value.filter(f =>
    f.featureId?.toLowerCase().includes(query) ||
    f.title?.toLowerCase().includes(query)
  )
})

const filteredAvailableTestCases = computed(() => {
  if (!testCaseSearchQuery.value.trim()) {
    return availableTestCases.value
  }
  const query = testCaseSearchQuery.value.toLowerCase()
  return availableTestCases.value.filter(tc =>
    tc.testCaseId?.toLowerCase().includes(query) ||
    tc.name?.toLowerCase().includes(query)
  )
})

// Feature linking functions
const loadAvailableFeatures = async () => {
  try {
    const { featureService } = await import('@/database/services')
    const allFeatures = await featureService.getByProject(projectId.value)
    // Exclude features already linked
    const linkedFeatureIds = relatedFeatures.value.map(f => f.id)
    availableFeatures.value = allFeatures.filter(f => !linkedFeatureIds.includes(f.id))
  } catch (error) {
    console.error('Error loading available features:', error)
    availableFeatures.value = []
  }
}

const selectFeatureToLink = (featureId) => {
  selectedFeatureId.value = featureId
  const selected = availableFeatures.value.find(f => f.id === featureId)
  if (selected) {
    featureSearchQuery.value = `${selected.featureId} - ${selected.title}`
  }
  showFeatureDropdown.value = false
}

const linkSelectedFeature = async () => {
  if (!selectedFeatureId.value) return
  
  try {
    const { featureService } = await import('@/database/services')
    const feature = await featureService.getById(selectedFeatureId.value)
    
    if (!feature) {
      showError('Feature not found')
      return
    }
    
    // Update feature's bug_ids array
    const bugIds = feature.bug_ids || []
    if (!bugIds.includes(bugId.value)) {
      bugIds.push(bugId.value)
      await featureService.update(selectedFeatureId.value, { bug_ids: bugIds })
      
      // ALSO Update bug's feature_ids array (bidirectional link)
      const featureIds = bug.value.feature_ids || []
      if (!featureIds.includes(selectedFeatureId.value)) {
        featureIds.push(selectedFeatureId.value)
        await bugService.update(bugId.value, { feature_ids: featureIds })
      }
      
      // Add activity to bug
      const userId = authStore.user?.id || 'system'
      await bugService.addActivity(bugId.value, {
        type: 'linked',
        user_id: userId,
        description: `linked feature ${feature.featureId || feature.title}`,
        timestamp: new Date().toISOString()
      })
    }
    
    showSuccess('Feature linked successfully')
    featureSearchQuery.value = ''
    selectedFeatureId.value = null
    await Promise.all([
      loadRelatedFeatures(),
      loadAvailableFeatures(),
      loadBug() // Reload bug to get updated activities
    ])
  } catch (error) {
    showError('Failed to link feature')
  }
}

const refreshFeatures = async () => {
  try {
    await Promise.all([
      loadRelatedFeatures(),
      loadAvailableFeatures()
    ])
  } catch (error) {
    console.error('Error refreshing features:', error)
    showError('Failed to refresh features')
  }
}

// Test case linking functions
const loadAvailableTestCases = async () => {
  try {
    const allTestCases = await testCaseService.getByProject(projectId.value)
    // Exclude test cases already linked
    const linkedTestCaseIds = (bug.value?.test_case_ids || [])
    availableTestCases.value = allTestCases.filter(tc => !linkedTestCaseIds.includes(tc.id))
  } catch (error) {
    console.error('Error loading available test cases:', error)
    availableTestCases.value = []
  }
}

const selectTestCaseToLink = (testCaseId) => {
  selectedTestCaseId.value = testCaseId
  const selected = availableTestCases.value.find(tc => tc.id === testCaseId)
  if (selected) {
    testCaseSearchQuery.value = `${selected.testCaseId} - ${selected.name}`
  }
  showTestCaseDropdown.value = false
}

const linkSelectedTestCase = async () => {
  if (!selectedTestCaseId.value) return
  
  try {
    const testCase = await testCaseService.getById(selectedTestCaseId.value)
    if (!testCase) {
      showError('Test case not found')
      return
    }
    
    // Update bug's test_case_ids array
    const testCaseIds = bug.value?.test_case_ids || []
    if (!testCaseIds.includes(selectedTestCaseId.value)) {
      testCaseIds.push(selectedTestCaseId.value)
      await bugService.update(bugId.value, { test_case_ids: testCaseIds })
      
      // Add activity to bug
      const userId = authStore.user?.id || 'system'
      await bugService.addActivity(bugId.value, {
        type: 'linked',
        user_id: userId,
        description: `linked test case ${testCase.testCaseId || testCase.name}`,
        timestamp: new Date().toISOString()
      })
    }
    
    // Update test case's bug IDs (bidirectional link)
    const bugIds = testCase.bug_ids || []
    if (!bugIds.includes(bugId.value)) {
      bugIds.push(bugId.value)
      await testCaseService.update(selectedTestCaseId.value, { bug_ids: bugIds })
    }
    
    showSuccess('Test case linked successfully')
    testCaseSearchQuery.value = ''
    selectedTestCaseId.value = null
    await Promise.all([
      loadBug(),
      loadAvailableTestCases()
    ])
  } catch (error) {
    console.error('Error linking test case:', error)
    showError('Failed to link test case')
  }
}

const unlinkFeature = async (featureId) => {
  if (!confirm('Are you sure you want to unlink this feature?')) return
  
  try {
    const { featureService } = await import('@/database/services')
    
    // Remove bug from feature's bug_ids array
    const feature = await featureService.getById(featureId)
    if (feature) {
      const bugIds = feature.bug_ids || []
      const updatedBugIds = bugIds.filter(id => id !== bugId.value)
      await featureService.update(featureId, { bug_ids: updatedBugIds })
    }
    
    // ALSO Remove feature from bug's feature_ids array (bidirectional unlink)
    const featureIds = bug.value?.feature_ids || []
    const updatedFeatureIds = featureIds.filter(id => id !== featureId)
    await bugService.update(bugId.value, { feature_ids: updatedFeatureIds })
    
    showSuccess('Feature unlinked successfully')
    await Promise.all([
      loadRelatedFeatures(),
      loadAvailableFeatures(),
      loadBug()
    ])
  } catch (error) {
    console.error('Error unlinking feature:', error)
    showError('Failed to unlink feature')
  }
}

const unlinkTestCase = async (testCaseId) => {
  if (!confirm('Are you sure you want to unlink this test case?')) return
  
  try {
    // Remove from bug's test_case_ids
    const testCaseIds = bug.value?.test_case_ids || []
    const updatedTestCaseIds = testCaseIds.filter(id => id !== testCaseId)
    await bugService.update(bugId.value, { test_case_ids: updatedTestCaseIds })
    
    // Remove bug from test case's bug_ids (bidirectional unlink)
    const testCase = await testCaseService.getById(testCaseId)
    if (testCase) {
      const bugIds = testCase.bug_ids || []
      const updatedBugIds = bugIds.filter(id => id !== bugId.value)
      await testCaseService.update(testCaseId, { bug_ids: updatedBugIds })
    }
    
    showSuccess('Test case unlinked successfully')
    await Promise.all([
      loadBug(),
      loadAvailableTestCases()
    ])
  } catch (error) {
    console.error('Error unlinking test case:', error)
    showError('Failed to unlink test case')
  }
}

const refreshTestCases = async () => {
  try {
    await Promise.all([
      loadBug(),
      loadAvailableTestCases()
    ])
  } catch (error) {
    console.error('Error refreshing test cases:', error)
    showError('Failed to refresh test cases')
  }
}

const formatDateTime = (date) => {
  if (!date) return ''
  const d = new Date(date)
  const now = new Date()
  const diffMs = now - d
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)
  
  // Less than 1 minute ago
  if (diffMins < 1) return 'Just now'
  // Less than 1 hour ago
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`
  // Less than 24 hours ago
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
  // Less than 7 days ago
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`
  
  // Format as full date with time
  const options = {
    month: 'short',
    day: 'numeric',
    year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    hour: '2-digit',
    minute: '2-digit'
  }
  return d.toLocaleString('en-US', options)
}

const formatFullDateTime = (date) => {
  if (!date) return ''
  const d = new Date(date)
  const options = {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  }
  return d.toLocaleString('en-US', options)
}

// Lifecycle
onMounted(async () => {
  await loadUsers()
  await loadBug()
  // Only load comments if bugId is available (loadBug sets it)
  if (bugId.value) {
  await loadComments()
  }
  await loadAvailableFeatures()
  await loadAvailableTestCases()
})

// Watch for route param changes (when navigating from one bug to another)
watch(() => route.params.bugId, async (newBugId, oldBugId) => {
  if (newBugId && newBugId !== oldBugId) {
    await loadBug()
    // Only load comments if bugId is available
    if (bugId.value) {
    await loadComments()
    }
    await loadAvailableFeatures()
    await loadAvailableTestCases()
  }
})
</script>
<style>
  .upload-container .hr-upload-file-list{
    display: none !important;
  }

  .hr-carousel {
    height: 600px !important;
  }
</style>
<style scoped>

/* Hide scrollbar by default, show only on hover */
.overflow-y-auto {
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;
}

.overflow-y-auto:hover {
  scrollbar-color: rgb(209 213 219) transparent;
}

.dark .overflow-y-auto:hover {
  scrollbar-color: rgb(75 85 99) transparent;
}

/* WebKit browsers */
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: transparent;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: transparent;
  border-radius: 4px;
}

.overflow-y-auto:hover::-webkit-scrollbar-thumb {
  background: rgb(209 213 219);
}

.dark .overflow-y-auto:hover::-webkit-scrollbar-thumb {
  background: rgb(75 85 99);
}

/* Height constraints for text sections */
.max-h-40 {
  max-height: 10rem;
}

.h-12 {
  height: 3rem;
}

/* Ensure HLSelect has visible border by default on page load */
.bug-detail-page :deep(.hr-select),
.bug-detail-page :deep([class*="hr-select"]) {
  border: 1px solid rgb(209 213 219) !important; /* border-gray-300 */
  border-radius: 0.375rem !important;
}

.bug-detail-page :deep(.hr-select [class*="hr-base-selection-input"]),
.bug-detail-page :deep([class*="hr-base-selection-input"]) {
  border: 1px solid rgb(209 213 219) !important; /* border-gray-300 */
  border-radius: 0.375rem !important;
}

.dark .bug-detail-page :deep(.hr-select),
.dark .bug-detail-page :deep(.hr-select [class*="hr-base-selection-input"]) {
  border-color: rgb(75 85 99) !important; /* border-gray-600 */
}

.bug-detail-page :deep(.hr-select:hover),
.bug-detail-page :deep(.hr-select [class*="hr-base-selection-input"]:hover) {
  border-color: rgb(59 130 246) !important; /* border-primary-500 */
}

.bug-detail-page :deep(.hr-select:focus-within),
.bug-detail-page :deep(.hr-select [class*="hr-base-selection-input"]:focus),
.bug-detail-page :deep(.hr-select [class*="hr-base-selection-input"]:focus-within) {
  border-color: rgb(59 130 246) !important; /* border-primary-500 */
  outline: 2px solid rgba(59, 130, 246, 0.2) !important;
  outline-offset: 0 !important;
}
</style>
